import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import Modal from '../components/Modal';
import VarietyDetailModal from '../components/VarietyDetailModal';
import PurchaseDetailsModal from '../components/PurchaseDetailsModal';
import DiscountModal from '../components/DiscountModal';
import OrderDiscountModal from '../components/OrderDiscountModal';
import DiscountAuthorizationModal from '../components/DiscountAuthorizationModal';
import PendingSalesTabs from '../components/PendingSalesTabs';
import DocumentTypeModal from '../components/DocumentTypeModal';
import TaxExclusionModal from '../components/TaxExclusionModal';
import CartPanel from '../components/CartPanel';
import CategoryFilterBar from '../components/CategoryFilterBar';
import DocumentHeader from '../components/DocumentHeader';
import ModalClientRegistration from '../components/ModalClientRegistration';
import ProductGrid from '../components/ProductGrid';
import ProductViewToggle from '../components/ProductViewToggle';
import ScaleStatusPanel from '../components/ScaleStatusPanel';
import PosModeTabs from '../components/PosModeTabs';
import PosSlotGrid from '../components/PosSlotGrid';
import PosSlotPickerModal from '../components/PosSlotPickerModal';
import PosClientView from '../components/PosClientView';
import useCurrentUser from '../hooks/useCurrentUser';
import useConfirmDialog from '../hooks/useConfirmDialog';
import useMyPreferences from '../hooks/useMyPreferences';
import useProductViewPreference from '../hooks/useProductViewPreference';
import useProductEvents from '../hooks/useProductEvents';
import usePosNavbarCart from '../hooks/usePosNavbarCart';
import useScaleStream from '../hooks/useScaleStream';
import usePosSyncSession from '../hooks/usePosSyncSession';
import useActivePurchasePreview from '../hooks/useActivePurchasePreview';
import { getProducts, getCategories, getCashRegisters, updatePurchase, getPurchaseById, convertPurchaseToQuotation, suspendPurchase, resumePurchase, sendPrintRequest, getBranchTaxes } from '../api';
import { FiPercent, FiPrinter, FiPauseCircle, FiCheckCircle, FiInfo, FiSearch, FiX } from 'react-icons/fi';
import {
    decodeVariableBarcode,
    resolveBarcodeMode,
    buildScaleBarcodeLookupKeys,
    buildShortBarcodeLookupKeys,
} from '../utils/barcode';
import { getBarcodePrefixMappings } from '../utils/barcodeSettings';
import { CLIENT_TYPE_OPTIONS, normalizeClientType, resolvePriceClientType, getClientTypeLabel } from '../utils/clientType';
import { hasPermission, isAdminUser } from '../utils/permissions';
import { normalizePaginatedResponse } from '../utils/pagination';
import InlineLoading from '../components/InlineLoading';
import { getPurchaseItems } from '../utils/purchaseItems';
import { notify } from '../utils/alertBus';
import { mergeProductMatrix } from '../utils/productMatrix';
import { applyProductEvent } from '../utils/productEventPatch';
import { getPrintMode, PRINT_MODE_REMOTE } from '../utils/printSettings';
import { normalizeQuantity } from '../utils/quantity';
import { computeTaxAdjustedTotals } from '../utils/taxTotals';
import {
    getPosRegisterStorageKey,
    getPosSessionStorageKey,
    resolveCurrentPosScope,
} from '../utils/posRegisterScope';
import {
    POS_VIEW_EDITOR,
    buildPosWindowName,
    buildPosViewSearchParams,
    normalizePosView,
} from '../utils/posSync';
import { isScaleWeightMetricUnit } from '../utils/scalePresentation';
import {
    MAX_PURCHASE_ITEM_QUANTITY,
    buildPurchaseItemQuantityLimitMessage,
    findInvalidPurchaseItemQuantity,
} from '../utils/purchaseItemLimits';
import {
    buildPosSlotLayoutScopeKey,
    buildPosSlotPickerEntries,
    buildResolvedPosSlots,
    getPosSlotLayout,
    setPosSlotLayout,
} from '../utils/posSlotLayouts';

const POS_PRODUCT_VIEW_STORAGE_KEY = 'posProductViewPreference';
const POS_PRODUCT_VIEWS = ['grid', 'slots'];

const EditPurchasePage = () => {
    const { id } = useParams(); // Get the purchase ID from the route
    const [searchParams, setSearchParams] = useSearchParams();
    const posView = normalizePosView(searchParams.get('posView'));
    const isEditorPosView = posView === POS_VIEW_EDITOR;
    const location = useLocation();
    const navigate = useNavigate();

    const [modalVariety, setModalVariety] = useState(null);
    const [modalVarietyPrice, setModalVarietyPrice] = useState(null);

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const [categoriesError, setCategoriesError] = useState(null);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [quantities, setQuantities] = useState({});
    const [totalAmount, setTotalPrice] = useState(0);
    const [showPurchaseModal, setShowPurchaseModal] = useState(false);
    const [purchaseDetails, setPurchaseDetails] = useState(null);
    const [isEditHydrated, setIsEditHydrated] = useState(false);

    const [clientId, setClientId] = useState(null);
    const [name, setName] = useState('');
    const [rut, setRut] = useState('');
    const [email, setEmail] = useState('');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [phone, setPhone] = useState('');
    const [giro, setGiro] = useState('');
    const [clientType, setClientType] = useState('detalle');
    const [showTypeModal, setShowTypeModal] = useState(false);
    const [typeSelection, setTypeSelection] = useState('');
    const [modalProduct, setModalProduct] = useState(null);

    const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');
    const searchInputRef = useRef(null);
    const barcodeInputRef = useRef(null);
    const hasShownConsolidationWarningRef = useRef(false);
    const purchaseCreatedAtRef = useRef(null);
    const [discounts, setDiscounts] = useState({});
    const [showDiscountModal, setShowDiscountModal] = useState(false);
    const [showDiscountAuthorizationModal, setShowDiscountAuthorizationModal] = useState(false);
    const [pendingDiscountAuthorization, setPendingDiscountAuthorization] = useState(null);
    const [pendingDiscountRequests, setPendingDiscountRequests] = useState({ items: {}, order: null });
    const [discountAuthorizationInitialState, setDiscountAuthorizationInitialState] = useState(null);
    const [discountAuthorizations, setDiscountAuthorizations] = useState({});
    const [orderDiscountAuthorizationId, setOrderDiscountAuthorizationId] = useState(null);
    const [addedOrder, setAddedOrder] = useState([]);
    const [searchVarietyResults, setSearchVarietyResults] = useState([]);
    const [itemsSubtotal, setItemsSubtotal] = useState(0);
    const [orderDiscountAmount, setOrderDiscountAmount] = useState(null);
    const [orderDiscountPercentage, setOrderDiscountPercentage] = useState(null);
    const [showOrderDiscountModal, setShowOrderDiscountModal] = useState(false);
    const [taxes, setTaxes] = useState([]);
    const [taxesLoading, setTaxesLoading] = useState(false);
    const [taxesError, setTaxesError] = useState(null);
    const [excludedTaxIds, setExcludedTaxIds] = useState([]);
    const [showTaxModal, setShowTaxModal] = useState(false);
    const [documentType, setDocumentType] = useState(null);
    const [linePriceMeta, setLinePriceMeta] = useState({});
    const [cashRegisterId, setCashRegisterId] = useState(null);
    const [cashRegisterSessionId, setCashRegisterSessionId] = useState(null);
    const [pendingTabsScopeReady, setPendingTabsScopeReady] = useState(false);
    const [cashRegisters, setCashRegisters] = useState([]);
    const [showDocumentTypeModal, setShowDocumentTypeModal] = useState(false);
    const [pendingDocumentType, setPendingDocumentType] = useState(null);
    const [pendingFinishAfterClient, setPendingFinishAfterClient] = useState(false);
    const [pendingConversionAfterClient, setPendingConversionAfterClient] = useState(false);
    const [showClientModal, setShowClientModal] = useState(false);
    const [showSlotPickerModal, setShowSlotPickerModal] = useState(false);
    const [slotModalState, setSlotModalState] = useState(null);
    const [activeScaleSelection, setActiveScaleSelection] = useState(null);
    const [lastStableScaleReading, setLastStableScaleReading] = useState(null);
    const [productView, setProductView] = useProductViewPreference({
        storageKey: POS_PRODUCT_VIEW_STORAGE_KEY,
        allowedViews: POS_PRODUCT_VIEWS,
    });
    const [showMobileSearch, setShowMobileSearch] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const { user } = useCurrentUser();
    const { confirm } = useConfirmDialog();
    const {
        preferences: myPreferences,
        savePreferences: saveMyPreferences,
    } = useMyPreferences();
    const isAdmin = isAdminUser(user);
    const canUpdateSale = hasPermission(user, 'sales.update');
    const canReadSales = hasPermission(user, 'sales.read');
    const canCreateSale = hasPermission(user, 'sales.create');
    const canDeleteSale = hasPermission(user, 'sales.delete');
    const canSell = hasPermission(user, 'pos.sell');
    const canReadClient = hasPermission(user, 'client.read');
    const canCreateClient = hasPermission(user, 'client.create');
    const canConvertToQuotation =
        hasPermission(user, 'quotation.convert_from_sale') || hasPermission(user, 'quotation.create');
    const canExcludeTaxes = hasPermission(user, 'tax.exclude');
    const canEditSale = canUpdateSale;
    const canChangeClientType = canCreateSale || canUpdateSale;
    const canOverrideLinePriceType = hasPermission(user, 'branch.access.any');
    const activeCashRegister = cashRegisters.find(
        (register) => String(register.id) === String(cashRegisterId)
    );
    const activePurchaseNumericId = Number(id);
    const activeSession = activeCashRegister?.activeSession || null;
    const isActiveSessionOwnedByUser = activeSession
        ? String(activeSession.openedBy) === String(user?.id)
        : false;
    const scaleState = useScaleStream({
        branchId,
        cashRegisterId,
        cashRegisterSessionId,
        enabled: Boolean(
            branchId
            && cashRegisterId
            && cashRegisterSessionId
            && isActiveSessionOwnedByUser
            && canSell
        ),
    });
    const posSyncEnabled = Boolean(
        branchId
        && cashRegisterId
        && cashRegisterSessionId
        && isActiveSessionOwnedByUser
        && canSell
    );
    const {
        connectionStatus: posSyncConnectionStatus,
        activePurchaseId: syncedActivePurchaseId,
        weighing: posSyncWeighing,
        preview: posSyncPreview,
        hasEditor: posSyncHasEditor,
        lastPurchaseChanged: posSyncLastPurchaseChanged,
        setActivePurchaseId: publishActivePurchaseId,
        setWeighing: publishSharedWeighing,
        setPreview: publishSharedPreview,
    } = usePosSyncSession({
        branchId,
        cashRegisterId,
        cashRegisterSessionId,
        userId: user?.id,
        enabled: posSyncEnabled,
        mode: posView,
    });
    useEffect(() => {
        const rawWeightKg = scaleState?.freshStableWeightKg ?? scaleState?.latestStableWeightKg;
        const weightKg = Number(rawWeightKg);
        if (rawWeightKg == null || !Number.isFinite(weightKg) || weightKg < 0) return;
        setLastStableScaleReading({
            weightKg,
            stableAt: scaleState?.freshStableAt || scaleState?.latestStableAt || new Date().toISOString(),
        });
    }, [
        scaleState?.freshStableAt,
        scaleState?.freshStableWeightKg,
        scaleState?.latestStableAt,
        scaleState?.latestStableWeightKg,
    ]);
    useEffect(() => {
        setLastStableScaleReading(null);
        setActiveScaleSelection(null);
    }, [branchId, cashRegisterId, cashRegisterSessionId]);
    const activePurchasePreviewRefreshToken =
        posSyncLastPurchaseChanged && Number(posSyncLastPurchaseChanged.purchaseId) === Number(syncedActivePurchaseId)
            ? `${posSyncLastPurchaseChanged.purchaseId}:${posSyncLastPurchaseChanged.updatedAt || ''}`
            : String(syncedActivePurchaseId || '');
    const {
        purchase: activePurchasePreview,
        loading: activePurchasePreviewLoading,
        error: activePurchasePreviewError,
    } = useActivePurchasePreview({
        branchId,
        activePurchaseId: syncedActivePurchaseId,
        enabled: posSyncEnabled && !isEditorPosView,
        refreshToken: activePurchasePreviewRefreshToken,
    });
    const helperSharedPurchase = posSyncPreview || activePurchasePreview || null;
    const helperSharedPurchaseLoading = !posSyncPreview && activePurchasePreviewLoading;
    const helperSharedPurchaseError = !posSyncPreview ? activePurchasePreviewError : null;
    const isSlotProductView = productView === 'slots';
    const normalizeLocalSearchTerm = useCallback((value) => String(value || '').toLowerCase(), []);
    const normalizeLocalCategory = useCallback((value) => {
        const normalized = String(value || '').trim();
        if (!normalized || normalized === 'null' || normalized === 'undefined') return 'all';
        return normalized.toLowerCase() === 'all' ? 'all' : normalized;
    }, []);
    const applySearch = useCallback((nextValue) => {
        const normalized = normalizeLocalSearchTerm(nextValue);
        if (normalized.trim()) {
            setProductView('grid');
        }
        setSearchTerm(normalized);
        return normalized;
    }, [normalizeLocalSearchTerm, setProductView]);
    const handleSearch = useCallback((event) => {
        applySearch(event?.target?.value ?? '');
    }, [applySearch]);
    const activateProductSearch = useCallback(() => {
        setProductView('grid');
    }, [setProductView]);
    const handleCategorySelect = useCallback((category) => {
        setSelectedCategory(normalizeLocalCategory(category));
    }, [normalizeLocalCategory]);
    const hasActiveSearch = Boolean((searchTerm || '').trim());
    const mobileSearchVisible = showMobileSearch || hasActiveSearch;
    useEffect(() => {
        if (isSlotProductView && hasActiveSearch) {
            setProductView('grid');
        }
    }, [hasActiveSearch, isSlotProductView, setProductView]);
    const allowedClientTypes = useMemo(() => {
        if (!Array.isArray(activeCashRegister?.allowedClientTypes)) return [];
        const normalized = [];
        const seen = new Set();
        activeCashRegister.allowedClientTypes.forEach((entry) => {
            const value = normalizeClientType(entry);
            if (!value || seen.has(value)) return;
            normalized.push(value);
            seen.add(value);
        });
        return normalized;
    }, [activeCashRegister?.allowedClientTypes]);
    const allowedClientTypeOptions = useMemo(() => {
        if (!allowedClientTypes.length) return CLIENT_TYPE_OPTIONS;
        return CLIENT_TYPE_OPTIONS.filter((option) => allowedClientTypes.includes(option.value));
    }, [allowedClientTypes]);
    const resolveAllowedClientType = useCallback((value) => {
        const normalized = normalizeClientType(value);
        if (!allowedClientTypes.length) return normalized;
        if (!normalized || !allowedClientTypes.includes(normalized)) {
            return allowedClientTypes[0] || '';
        }
        return normalized;
    }, [allowedClientTypes]);

    const resolveSaleType = useCallback(
        (value) => resolveAllowedClientType(value) || 'detalle',
        [resolveAllowedClientType]
    );

    const availabilityForCategory = (category) =>
        category?.CategoryAvailabilities
        || category?.CategoryAvailability
        || [];
    const categoryIdByName = useMemo(() => {
        const map = new Map();
        (categories || []).forEach((category) => {
            const name = category?.name ? String(category.name).trim().toLowerCase() : '';
            if (name) {
                map.set(name, category.id);
            }
        });
        return map;
    }, [categories]);
    const resolveProductCategoryId = useCallback((product) => {
        if (Number.isInteger(product?.categoryId)) return product.categoryId;
        const name = product?.category ? String(product.category).trim().toLowerCase() : '';
        if (!name) return null;
        return categoryIdByName.get(name) || null;
    }, [categoryIdByName]);
    const allowedCategoryIds = useMemo(() => {
        if (!cashRegisterId || !branchId || !(categories || []).length) return null;
        const resolvedBranchId = Number(branchId);
        const resolvedRegisterId = Number(cashRegisterId);
        if (!Number.isFinite(resolvedBranchId) || !Number.isFinite(resolvedRegisterId)) return null;
        const allowed = new Set();
        (categories || []).forEach((category) => {
            if (category?.isActive === false) return;
            const rows = availabilityForCategory(category);
            const hasAccess = rows.some((row) => {
                if (row?.isActive === false) return false;
                if (Number(row.branchId) !== resolvedBranchId) return false;
                if (row.cashRegisterId == null) return true;
                return Number(row.cashRegisterId) === resolvedRegisterId;
            });
            if (hasAccess) {
                allowed.add(category.id);
            }
        });
        return allowed;
    }, [branchId, cashRegisterId, categories]);
    const availableProducts = useMemo(() => {
        if (!allowedCategoryIds) return products;
        if (allowedCategoryIds.size === 0) return [];
        return (products || []).filter((product) =>
            allowedCategoryIds.has(resolveProductCategoryId(product))
        );
    }, [products, allowedCategoryIds, resolveProductCategoryId]);
    const categoriesForDisplay = useMemo(() => {
        const active = (categories || []).filter((category) => category?.isActive !== false);
        if (!allowedCategoryIds) return active;
        return active.filter((category) => allowedCategoryIds.has(category.id));
    }, [categories, allowedCategoryIds]);
    const categoryIdsWithProducts = useMemo(() => {
        const ids = new Set();
        (availableProducts || []).forEach((product) => {
            const id = resolveProductCategoryId(product);
            if (Number.isInteger(id)) ids.add(id);
        });
        return ids;
    }, [availableProducts, resolveProductCategoryId]);
    const displayCategories = useMemo(
        () => categoriesForDisplay.filter((category) => categoryIdsWithProducts.has(category.id)),
        [categoriesForDisplay, categoryIdsWithProducts]
    );
    const categoriesToRender = useMemo(() => {
        if (searchTerm || selectedCategory === 'all') return displayCategories;
        return displayCategories.filter(
            (category) => String(category.id) === String(selectedCategory)
        );
    }, [displayCategories, searchTerm, selectedCategory]);
    const isSellablePrice = (price) => price && price.price !== null && price.price !== undefined;
    // Invariant: one active UI line per (varietyId, priceIndex) slot.
    const lineKey = (varietyId, priceIndex) => `${varietyId}:${priceIndex}`;
    const sortLineItemsByCartIdentity = (items) => {
        const orderIndexByKey = new Map();
        (addedOrder || []).forEach((entry, index) => {
            if (!Number.isInteger(entry?.varietyId) || !Number.isInteger(entry?.priceIndex)) return;
            const key = lineKey(entry.varietyId, entry.priceIndex);
            if (!orderIndexByKey.has(key)) orderIndexByKey.set(key, index);
        });

        return [...(items || [])].sort((a, b) => {
            const keyA = lineKey(a?.varietyId, a?.priceIndex);
            const keyB = lineKey(b?.varietyId, b?.priceIndex);
            const orderA = orderIndexByKey.get(keyA);
            const orderB = orderIndexByKey.get(keyB);
            const hasOrderA = Number.isInteger(orderA);
            const hasOrderB = Number.isInteger(orderB);
            if (hasOrderA && hasOrderB) return orderA - orderB;
            if (hasOrderA) return -1;
            if (hasOrderB) return 1;

            const varietyA = Number(a?.varietyId) || 0;
            const varietyB = Number(b?.varietyId) || 0;
            if (varietyA !== varietyB) return varietyA - varietyB;
            const priceIndexA = Number(a?.priceIndex) || 0;
            const priceIndexB = Number(b?.priceIndex) || 0;
            return priceIndexA - priceIndexB;
        });
    };
    const orderAndIndexLineItems = (items) =>
        sortLineItemsByCartIdentity(items).map((item, lineIndex) => ({ ...item, lineIndex }));
    const normalizePriceTypeValue = (value) => normalizeClientType(value) || '';
    const getPriceTypeLabel = (type) => getClientTypeLabel(type) || type;
    const resolveLinePriceMeta = (varietyId, priceIndex) => {
        const key = lineKey(varietyId, priceIndex);
        return linePriceMeta[key] || null;
    };
    const normalizeBarcodeValue = (value) => {
        if (value === null || value === undefined) return null;
        const normalized = String(value).trim();
        return normalized === '' ? null : normalized;
    };
    const varietySupportsBarcodePriceTypeSelection = (variety) => {
        const prices = (variety?.VarietyPrices || []).filter((price) => isSellablePrice(price));
        if (prices.length < 2) return false;
        const barcodes = prices.map((price) => normalizeBarcodeValue(price?.barcode));
        if (!barcodes.length || barcodes.some((barcode) => !barcode)) return false;
        return new Set(barcodes).size === 1;
    };
    const applyBarcodeAutoPriceSelectionMeta = (variety, priceIndex) => {
        if (!Number.isInteger(variety?.id) || !Number.isInteger(priceIndex) || priceIndex < 0) return;
        const key = lineKey(variety.id, priceIndex);
        const price = variety?.VarietyPrices?.[priceIndex];
        const defaultPriceType = normalizePriceTypeValue(resolvePriceClientType(price));
        const autoPriceSelection = varietySupportsBarcodePriceTypeSelection(variety);
        setLinePriceMeta((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                priceType: normalizePriceTypeValue(prev[key]?.priceType) || defaultPriceType,
                priceTypeOverride: Boolean(prev[key]?.priceTypeOverride),
                autoPriceSelection,
            },
        }));
    };
    const isPriceAllowedByRegister = (price) => {
        if (!isSellablePrice(price)) return false;
        const priceType = resolvePriceClientType(price);
        if (allowedClientTypes.length && !allowedClientTypes.includes(priceType)) return false;
        return true;
    };
    const isPriceAllowedForType = (type, price) => {
        if (!isPriceAllowedByRegister(price)) return false;
        const priceType = resolvePriceClientType(price);
        if (!type) return true;
        return priceType === type;
    };
    const hasSellablePrice = (variety, type = clientType) =>
        (variety.VarietyPrices || []).some((price) => isPriceAllowedForType(type, price));
    const getVarietyPricesForFilterType = (variety, type = clientType) =>
        (variety.VarietyPrices || []).filter((price) => isPriceAllowedForType(type, price));

    const varietyTaxProfileById = useMemo(() => {
        if (!canExcludeTaxes || !excludedTaxIds.length || !taxes.length) {
            return new Map();
        }
        const activeTaxes = (taxes || []).filter(
            (tax) => tax && tax.isActive !== false && tax.branchActive !== false
        );
        const taxesById = new Map(
            activeTaxes
                .map((tax) => [Number(tax.id), tax])
                .filter(([id]) => Number.isFinite(id))
        );
        const defaultTaxIds = activeTaxes
            .filter((tax) => tax.applyToAllProducts)
            .map((tax) => Number(tax.id))
            .filter((id) => Number.isFinite(id));
        const excludedSet = new Set(
            (excludedTaxIds || [])
                .map((id) => Number(id))
                .filter((id) => Number.isFinite(id))
        );
        const sumRate = (list) =>
            list.reduce((sum, tax) => {
                const rate = Number(tax?.rate);
                return sum + (Number.isFinite(rate) ? rate : 0);
            }, 0);

        const map = new Map();
        (products || []).forEach((product) => {
            const useDefaultTaxes = product?.useDefaultTaxes !== false;
            let taxIds = useDefaultTaxes ? defaultTaxIds : (product?.taxIds || []);
            taxIds = (taxIds || [])
                .map((id) => Number(id))
                .filter((id) => taxesById.has(id));
            if (!useDefaultTaxes && taxIds.length === 0) {
                taxIds = defaultTaxIds;
            }
            const fullTaxes = taxIds.map((id) => taxesById.get(id)).filter(Boolean);
            const appliedTaxes = excludedSet.size
                ? fullTaxes.filter((tax) => !excludedSet.has(Number(tax.id)))
                : fullTaxes;
            const fullRate = sumRate(fullTaxes) / 100;
            const remainingRate = sumRate(appliedTaxes) / 100;
            const appliesTaxExclusion = excludedSet.size
                && fullTaxes.length
                && appliedTaxes.length !== fullTaxes.length
                && fullRate > 0;

            (product?.Varieties || []).forEach((variety) => {
                if (!Number.isInteger(variety?.id)) return;
                map.set(variety.id, {
                    fullRate,
                    remainingRate,
                    appliesTaxExclusion,
                });
            });
        });

        return map;
    }, [products, taxes, excludedTaxIds, canExcludeTaxes]);

    const resolveCartItemPricing = useCallback((item, variety, quantityValue) => {
        const quantity = normalizeQuantity(quantityValue ?? item?.quantity ?? 0);
        const price = Number(item?.price);
        const unitPrice = Number.isFinite(price) ? price : 0;
        const discount = Number(item?.discount);
        const unitDiscount = Number.isFinite(discount) ? discount : 0;
        const discountedUnitPrice = unitPrice - unitDiscount;
        const baseGross = Math.floor(unitPrice * quantity);
        const discountedGross = Math.floor(discountedUnitPrice * quantity);
        const varietyId = Number.isInteger(item?.varietyId) ? item.varietyId : variety?.id;
        const profile = varietyTaxProfileById.get(varietyId);
        if (!profile?.appliesTaxExclusion) {
            return {
                unitPrice,
                discountedUnitPrice,
                subtotal: discountedGross,
                taxFreeApplied: false,
            };
        }

        const adjustGross = (grossValue) => {
            const netFromFull = Math.round(grossValue / (1 + profile.fullRate));
            return Math.round(netFromFull * (1 + profile.remainingRate));
        };

        const adjustedGross = adjustGross(baseGross);
        const adjustedDiscountedGross = adjustGross(discountedGross);
        return {
            unitPrice: quantity > 0 ? adjustedGross / quantity : unitPrice,
            discountedUnitPrice: quantity > 0 ? adjustedDiscountedGross / quantity : discountedUnitPrice,
            subtotal: adjustedDiscountedGross,
            taxFreeApplied: true,
        };
    }, [varietyTaxProfileById]);

    const resolveVarietyPriceDisplay = useCallback((variety, varietyPrice, unitDiscount = 0) => {
        const basePriceRaw = Number(varietyPrice?.price);
        const basePrice = Number.isFinite(basePriceRaw) ? basePriceRaw : 0;
        const discountRaw = Number(unitDiscount);
        const resolvedDiscount = Number.isFinite(discountRaw) ? discountRaw : 0;
        const effectivePrice = basePrice - resolvedDiscount;
        const profile = varietyTaxProfileById.get(variety?.id);
        if (!profile?.appliesTaxExclusion) {
            return { price: effectivePrice, taxFreeApplied: false };
        }
        const grossValue = Math.floor(effectivePrice);
        const netFromFull = Math.round(grossValue / (1 + profile.fullRate));
        const adjustedGross = Math.round(netFromFull * (1 + profile.remainingRate));
        return { price: adjustedGross, taxFreeApplied: true };
    }, [varietyTaxProfileById]);
    const posSlotLayoutScopeKey = useMemo(
        () => buildPosSlotLayoutScopeKey(branchId, cashRegisterId),
        [branchId, cashRegisterId]
    );
    const currentPosSlotLayout = useMemo(
        () => getPosSlotLayout(myPreferences?.posSlotLayouts, branchId, cashRegisterId, availableProducts),
        [availableProducts, branchId, cashRegisterId, myPreferences?.posSlotLayouts]
    );
    const saveCurrentPosSlotLayout = useCallback(async (nextLayout) => {
        if (!posSlotLayoutScopeKey) {
            notify('Selecciona una caja activa antes de guardar favs.');
            return;
        }
        const nextLayouts = setPosSlotLayout(
            myPreferences?.posSlotLayouts,
            branchId,
            cashRegisterId,
            nextLayout,
            availableProducts
        );
        await saveMyPreferences({ posSlotLayouts: nextLayouts });
    }, [
        availableProducts,
        branchId,
        cashRegisterId,
        myPreferences?.posSlotLayouts,
        posSlotLayoutScopeKey,
        saveMyPreferences,
    ]);
    const posSlotPickerEntries = useMemo(() => buildPosSlotPickerEntries({
        products: availableProducts,
        isPriceAllowedByRegister,
        resolveDisplayPrice: resolveVarietyPriceDisplay,
    }), [availableProducts, isPriceAllowedByRegister, resolveVarietyPriceDisplay]);
    const resolvedPosSlots = useMemo(() => buildResolvedPosSlots({
        products: availableProducts,
        slotLayout: currentPosSlotLayout,
        quantities,
        discounts,
        isPriceAllowedByRegister,
        resolveDisplayPrice: resolveVarietyPriceDisplay,
    }), [
        availableProducts,
        currentPosSlotLayout,
        discounts,
        isPriceAllowedByRegister,
        quantities,
        resolveVarietyPriceDisplay,
    ]);

    const computeTotals = (
        productsData,
        quantitiesData,
        discountsData,
        overrideOrderDiscountAmount,
        overrideOrderDiscountPercentage
    ) => {
        const resolvedProducts = Array.isArray(productsData) ? productsData : products;
        const resolvedQuantities = quantitiesData ?? quantities;
        const resolvedDiscounts = discountsData ?? discounts;
        const resolvedOrderDiscountAmount =
            overrideOrderDiscountAmount !== undefined ? overrideOrderDiscountAmount : orderDiscountAmount;
        const resolvedOrderDiscountPercentage =
            overrideOrderDiscountPercentage !== undefined
                ? overrideOrderDiscountPercentage
                : orderDiscountPercentage;

        if (canExcludeTaxes && taxes.length) {
            return computeTaxAdjustedTotals({
                products: resolvedProducts,
                quantities: resolvedQuantities,
                discounts: resolvedDiscounts,
                taxes,
                excludedTaxIds,
                orderDiscountAmount: resolvedOrderDiscountAmount,
                orderDiscountPercentage: resolvedOrderDiscountPercentage,
                isPriceAllowed: (price) => isPriceAllowedByRegister(price),
            });
        }

        const subtotal = Math.floor((resolvedProducts || []).reduce((acc, product) => {
            return acc + product.Varieties.reduce((varietyAcc, variety) => {
                return varietyAcc + variety.VarietyPrices.reduce((priceAcc, price, index) => {
                    if (!isPriceAllowedByRegister(price)) return priceAcc;
                    const unitDiscount = resolvedDiscounts?.[variety.id]?.[index] ?? 0;
                    const qty = normalizeQuantity(
                        resolvedQuantities?.[variety.id]?.[index] ?? 0
                    );
                    return priceAcc + (price.price - unitDiscount) * qty;
                }, 0);
            }, 0);
        }, 0));

        const discountAmountToUse = (() => {
            const percentToUse =
                typeof resolvedOrderDiscountPercentage === 'number'
                    ? resolvedOrderDiscountPercentage
                    : null;

            if (typeof percentToUse === 'number') {
                return Math.floor(subtotal * (percentToUse / 100));
            }

            const amount =
                typeof resolvedOrderDiscountAmount === 'number'
                    ? resolvedOrderDiscountAmount
                    : 0;
            return Math.min(Math.max(amount, 0), subtotal);
        })();

        const total = Math.max(subtotal - discountAmountToUse, 0);
        return { subtotal, total };
    };

    useEffect(() => {
        if (!canExcludeTaxes) return;
        const totals = computeTotals(
            products,
            quantities,
            discounts,
            orderDiscountAmount,
            orderDiscountPercentage
        );
        setItemsSubtotal(totals.subtotal);
        setTotalPrice(totals.total);
    }, [excludedTaxIds, taxes]);

    useEffect(() => {
        if (!isEditHydrated) return;
        if (!canExcludeTaxes || !canEditSale || !id) return;
        if (!purchaseDetails) return;
        const totals = computeTotals(
            products,
            quantities,
            discounts,
            orderDiscountAmount,
            orderDiscountPercentage
        );
        handleSave(
            false,
            quantities,
            totals.total,
            discounts,
            null,
            null,
            discountAuthorizations,
            orderDiscountAuthorizationId,
            orderDiscountAmount,
            orderDiscountPercentage
        );
    }, [excludedTaxIds, isEditHydrated]);

    const refreshProducts = useCallback(async () => {
        if (!branchId) return;
        try {
            const productList = await getProducts(branchId, { pageSize: 0 });
            const normalized = normalizePaginatedResponse(productList);
            const productsData = normalized.data || [];
            const nextQuantities = mergeProductMatrix(productsData, products, quantities, 0);
            const nextDiscounts = mergeProductMatrix(productsData, products, discounts, 0);
            const nextAuthorizations = mergeProductMatrix(productsData, products, discountAuthorizations, null);

            setProducts(productsData);
            setFilteredProducts(productsData);
            setQuantities(nextQuantities);
            setDiscounts(nextDiscounts);
            setDiscountAuthorizations(nextAuthorizations);

            const totals = computeTotals(
                productsData,
                nextQuantities,
                nextDiscounts,
                orderDiscountAmount,
                orderDiscountPercentage
            );
            setItemsSubtotal(totals.subtotal);
            setTotalPrice(totals.total);
        } catch (error) {
            if (error.response?.status === 401) {
                navigate('/login');
                return;
            }
            console.error('Error refreshing products:', error);
        }
    }, [
        branchId,
        products,
        quantities,
        discounts,
        discountAuthorizations,
        orderDiscountAmount,
        orderDiscountPercentage,
        navigate,
    ]);

    const buildEmptyMatrices = (productList) =>
        (productList || []).reduce((acc, product) => {
            product.Varieties.forEach((variety) => {
                acc[variety.id] = new Array(variety.VarietyPrices.length).fill(0);
            });
            return acc;
        }, {});

    const buildEmptyAuthorizationMatrices = (productList) =>
        (productList || []).reduce((acc, product) => {
            product.Varieties.forEach((variety) => {
                acc[variety.id] = new Array(variety.VarietyPrices.length).fill(null);
            });
            return acc;
        }, {});

    const closeModalVariety = () => {
        setModalVariety(null);
        setSlotModalState(null);
        setActiveScaleSelection(null);
        applySearch('');
    };

    const closeModal = () => {
        setModalVarietyPrice(null);
        setSlotModalState(null);
        applySearch('');
    };

    const closeAllProductModals = () => {
        setModalVarietyPrice(null);
        setModalVariety(null);
        setModalProduct(null);
        setSlotModalState(null);
        setActiveScaleSelection(null);
        setShowDiscountModal(false);
        setShowSlotPickerModal(false);
        applySearch('');
        setTimeout(() => {
            searchInputRef?.current?.focus();
        }, 100);
    };

    const currentSearchQuery = searchParams.toString();
    const buildPathWithCurrentSearch = useCallback((pathname) => (
        currentSearchQuery ? `${pathname}?${currentSearchQuery}` : pathname
    ), [currentSearchQuery]);
    const posWindowName = useMemo(() => buildPosWindowName({
        branchId,
        cashRegisterId,
        cashRegisterSessionId,
        mode: posView,
    }), [branchId, cashRegisterId, cashRegisterSessionId, posView]);
    useEffect(() => {
        if (typeof window === 'undefined') return;
        window.name = posWindowName;
    }, [posWindowName]);
    const handlePosViewChange = useCallback((nextView) => {
        if (nextView === posView) return;
        const nextParams = buildPosViewSearchParams(searchParams, nextView);
        const nextSearch = nextParams.toString();
        const targetPath = `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
        const targetWindowName = buildPosWindowName({
            branchId,
            cashRegisterId,
            cashRegisterSessionId,
            mode: nextView,
        });
        const targetWindow = window.open(targetPath, targetWindowName);
        targetWindow?.focus();
    }, [branchId, cashRegisterId, cashRegisterSessionId, location.pathname, posView, searchParams]);

    const filterProducts = (search, category) => {
        let filtered = availableProducts;
        let varietyResults = [];

        const trimmedSearch = (search || '').trim().toLowerCase();

        if (trimmedSearch) {
            filtered = availableProducts.filter(product =>
                product.productName.toLowerCase().split(' ').some(word => word.startsWith(trimmedSearch))
            );
            const matchingProductIds = new Set(filtered.map((product) => String(product.id)));
            const seenVarietyKeys = new Set();

            // Search in variety names and include all varieties of matching products
            varietyResults = availableProducts.flatMap((product) =>
                (product.Varieties || [])
                    .filter(
                        (variety) =>
                            hasSellablePrice(variety, clientType)
                            && (
                                matchingProductIds.has(String(product.id))
                                || (
                                    variety.varietyName
                                    && variety.varietyName.toLowerCase().includes(trimmedSearch)
                                )
                            )
                    )
                    .map((variety) => ({ product, variety }))
                    .filter((entry) => {
                        const key = `${entry.product?.id || 'x'}-${entry.variety?.id || 'x'}`;
                        if (seenVarietyKeys.has(key)) return false;
                        seenVarietyKeys.add(key);
                        return true;
                    })
            );

        } else if (category !== 'all') {
            filtered = availableProducts.filter(
                (product) => String(resolveProductCategoryId(product)) === String(category)
            );
        }

        setFilteredProducts(filtered);
        setSearchVarietyResults(varietyResults);
    };

    const handleVarietySelection = ({ variety, product }) => {
        if (isVarietySoldOut(variety)) {
            notify('Esta variedad está agotada.');
            return;
        }
        const sellablePrices = getVarietyPricesForFilterType(variety);
        if (sellablePrices.length === 0) {
            if (clientType) {
                notify(`No hay precios para el filtro ${getPriceTypeLabel(clientType)}.`);
            } else {
                notify('No hay precios disponibles para esta variedad.');
            }
            return;
        }
        setSlotModalState(null);
        setModalVariety(variety);
        setModalVarietyPrice(null);
    };

    const findVarietyPriceByBarcode = (lookupKeys) => {
        if (!Array.isArray(lookupKeys) || lookupKeys.length === 0) return null;
        const keySet = new Set(lookupKeys.filter(Boolean));

        for (const product of availableProducts) {
            for (const variety of product.Varieties || []) {
                for (let index = 0; index < (variety.VarietyPrices || []).length; index += 1) {
                    const price = variety.VarietyPrices[index];
                    if (!isPriceAllowedForType(clientType, price)) continue;
                    const barcode = price?.barcode == null ? null : String(price.barcode).trim();
                    if (barcode && keySet.has(barcode)) {
                        return { variety, varietyPrice: price, priceIndex: index };
                    }
                }
            }
        }

        return null;
    };

    const findAnyVarietyPriceByBarcode = (lookupKeys) => {
        if (!Array.isArray(lookupKeys) || lookupKeys.length === 0) return null;
        const keySet = new Set(lookupKeys.filter(Boolean));

        for (const product of availableProducts) {
            for (const variety of product.Varieties || []) {
                for (let index = 0; index < (variety.VarietyPrices || []).length; index += 1) {
                    const price = variety.VarietyPrices[index];
                    if (!isPriceAllowedByRegister(price)) continue;
                    const barcode = price?.barcode == null ? null : String(price.barcode).trim();
                    if (barcode && keySet.has(barcode)) {
                        return { variety, varietyPrice: price, priceIndex: index };
                    }
                }
            }
        }

        return null;
    };

    const handleQuickEntryKeyDown = (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        const trimmed = (searchTerm || '').trim();
        if (!trimmed || !/^\d+$/.test(trimmed)) return;

        let match = null;
        let anyMatch = null;

        if (trimmed.length === 13) {
            const mappings = getBarcodePrefixMappings();
            const mode = resolveBarcodeMode(trimmed, mappings);
            if (mode) {
                const decoded = decodeVariableBarcode(trimmed, mode);
                if (decoded.isValid && decoded.isScale) {
                    const keys = buildScaleBarcodeLookupKeys(decoded);
                    match = findVarietyPriceByBarcode(keys);
                    if (!match) {
                        anyMatch = findAnyVarietyPriceByBarcode(keys);
                    }
                    const targetMatch = match || anyMatch;
                    if (targetMatch) {
                        if (!match && anyMatch && clientType) {
                            notify('El producto se agrego, pero su precio no coincide con el filtro activo.');
                        }
                        applyBarcodeAutoPriceSelectionMeta(targetMatch.variety, targetMatch.priceIndex);
                        const currentQty = normalizeQuantity(
                            quantities[targetMatch.variety.id]?.[targetMatch.priceIndex] || 0
                        );
                        if (mode === 'weight') {
                            const nextQty = normalizeQuantity(currentQty + decoded.weightKg, 3);
                            handleQuantityChange(targetMatch.variety.id, targetMatch.priceIndex, nextQty);
                        } else if (mode === 'amount') {
                            const nextQty = normalizeQuantity(currentQty + decoded.amount, 3);
                            handleQuantityChange(targetMatch.variety.id, targetMatch.priceIndex, nextQty);
                        } else if (mode === 'price') {
                            const unitPrice = targetMatch.varietyPrice?.price || 0;
                            if (unitPrice > 0) {
                                const qtyDelta = normalizeQuantity(decoded.price / unitPrice, 3);
                                const nextQty = normalizeQuantity(currentQty + qtyDelta, 3);
                                handleQuantityChange(targetMatch.variety.id, targetMatch.priceIndex, nextQty);
                            }
                        }
                        match = targetMatch;
                    }
                }
            }
        }

        if (!match && trimmed.length <= 7) {
            const keys = buildShortBarcodeLookupKeys(trimmed);
            match = findVarietyPriceByBarcode(keys);
            if (!match) {
                anyMatch = findAnyVarietyPriceByBarcode(keys);
            }
            const targetMatch = match || anyMatch;
            if (targetMatch) {
                if (!match && anyMatch && clientType) {
                    notify('El producto se agrego, pero su precio no coincide con el filtro activo.');
                }
                applyBarcodeAutoPriceSelectionMeta(targetMatch.variety, targetMatch.priceIndex);
                handleQuantityIncrement(targetMatch.variety.id, targetMatch.priceIndex, 1);
                match = targetMatch;
            }
        }

        if (match) {
            applySearch('');
            barcodeInputRef.current?.focus();
            barcodeInputRef.current?.select();
        }
    };

    const handleRemoveItem = async (itemToRemove) => {
        if (!canUpdateSale) return;
        if (!id) return;
        const cashRegisterPayload = resolveCashRegisterPayload();
        if (!cashRegisterPayload) return;

        const purchaseItems = getPurchaseItems(purchaseDetails);
        const variety = findVarietyForItem(itemToRemove);
        const resolvedPriceIndex = Number.isInteger(itemToRemove.priceIndex)
            ? itemToRemove.priceIndex
            : resolveItemPriceIndex(variety, itemToRemove);
        if (!Number.isInteger(resolvedPriceIndex) || resolvedPriceIndex < 0) {
            console.warn('Cannot remove item without a valid price index.', itemToRemove);
            notify('Advertencia: item sin índice válido. No se puede eliminar de forma segura.');
            return;
        }

        const updatedQuantities = { ...quantities };
        const updatedDiscounts = { ...discounts };

        if (variety && resolvedPriceIndex !== -1) {
            updatedQuantities[variety.id] = [...(updatedQuantities[variety.id] || [])];
            updatedQuantities[variety.id][resolvedPriceIndex] = 0;

            updatedDiscounts[variety.id] = [...(updatedDiscounts[variety.id] || [])];
            updatedDiscounts[variety.id][resolvedPriceIndex] = 0;
        }

        const updatedOrder = addedOrder.filter(
            i => !(i.varietyId === itemToRemove.varietyId && i.priceIndex === resolvedPriceIndex)
        );

        const updatedItems = purchaseItems.filter((item) => {
            if (
                Number.isInteger(itemToRemove.lineIndex)
                && Number.isInteger(item.lineIndex)
                && item.lineIndex === itemToRemove.lineIndex
            ) {
                return false;
            }
            if (
                Number.isInteger(itemToRemove.varietyPriceId)
                && Number.isInteger(item.varietyPriceId)
                && item.varietyId === itemToRemove.varietyId
                && item.varietyPriceId === itemToRemove.varietyPriceId
            ) {
                return false;
            }
            const itemIndex = Number.isInteger(item.priceIndex)
                ? item.priceIndex
                : resolveItemPriceIndex(findVarietyForItem(item), item);
            return !(item.varietyId === itemToRemove.varietyId && itemIndex === resolvedPriceIndex);
        });

        const updatedTotals = computeTotals(
            products,
            updatedQuantities,
            updatedDiscounts,
            orderDiscountAmount,
            orderDiscountPercentage
        );
        const updatedItemsSubtotal = updatedTotals.subtotal;
        const updatedTotal = updatedTotals.total;
        setItemsSubtotal(updatedTotals.subtotal);
        setTotalPrice(updatedTotals.total);

        setQuantities(updatedQuantities);
        setDiscounts(updatedDiscounts);
        setAddedOrder(updatedOrder);
        setLinePriceMeta((prev) => {
            const next = { ...prev };
            delete next[lineKey(itemToRemove.varietyId, resolvedPriceIndex)];
            return next;
        });
        setPurchaseDetails((prev) => ({
            ...prev,
            purchaseItems: updatedItems,
            itemsSubtotal: updatedItemsSubtotal,
            totalAmount: updatedTotal
        }));

        try {
            await updatePurchase(branchId, id, {
                purchaseItems: updatedItems,
                totalAmount: updatedTotal,
                clientId,
                order: updatedOrder,
                orderDiscountAmount,
                orderDiscountPercentage,
                ...cashRegisterPayload,
                ...(canExcludeTaxes ? { excludedTaxIds } : {}),
            });
        } catch (error) {
            console.error("Error updating purchase:", error);
            notify('Error al actualizar la venta.');
        }
    };

    useEffect(() => {
        filterProducts(searchTerm, selectedCategory);
    }, [searchTerm, selectedCategory, products, clientType]);

    const handleProductEvent = useCallback((event) => {
        const nextProducts = applyProductEvent(products, event);
        if (!nextProducts) {
            refreshProducts();
            return;
        }
        if (nextProducts === products) return;
        const nextQuantities = mergeProductMatrix(nextProducts, products, quantities, 0);
        const nextDiscounts = mergeProductMatrix(nextProducts, products, discounts, 0);
        const nextAuthorizations = mergeProductMatrix(nextProducts, products, discountAuthorizations, null);

        setProducts(nextProducts);
        setQuantities(nextQuantities);
        setDiscounts(nextDiscounts);
        setDiscountAuthorizations(nextAuthorizations);

        const totals = computeTotals(
            nextProducts,
            nextQuantities,
            nextDiscounts,
            orderDiscountAmount,
            orderDiscountPercentage
        );
        setItemsSubtotal(totals.subtotal);
        setTotalPrice(totals.total);
    }, [
        products,
        quantities,
        discounts,
        discountAuthorizations,
        orderDiscountAmount,
        orderDiscountPercentage,
        refreshProducts,
    ]);

    useProductEvents(branchId, handleProductEvent);

    useEffect(() => {
        let isCancelled = false;
        const fetchData = async () => {
            setIsEditHydrated(false);
            setPurchaseDetails(null);
            purchaseCreatedAtRef.current = null;
            setItemsSubtotal(0);
            setTotalPrice(0);
            try {
                // Fetch product list
                const productList = await getProducts(branchId, { pageSize: 0 });
                if (isCancelled) return;
                const normalized = normalizePaginatedResponse(productList);
                const productsData = normalized.data || [];
                setProducts(productsData);
                setFilteredProducts(productsData);

                if (!isEditorPosView) {
                    setAddedOrder([]);
                    setQuantities(buildEmptyMatrices(productsData));
                    setDiscounts(buildEmptyMatrices(productsData));
                    setDiscountAuthorizations(buildEmptyAuthorizationMatrices(productsData));
                    setLinePriceMeta({});
                    return;
                }

                // Fetch purchase details by ID
                let purchase = await getPurchaseById(branchId, id);
                if (isCancelled) return;
                if (purchase.saleStatus === 'suspended') {
                    const confirmed = await confirm({
                        title: 'Reanudar venta',
                        message: 'Esta venta esta suspendida. ¿Reanudarla?',
                        confirmLabel: 'Reanudar',
                        cancelLabel: 'Cancelar',
                        tone: 'warning',
                    });
                    if (!confirmed) {
                        navigate(`/branches/${branchId}/purchases`);
                        return;
                    }
                    try {
                        const resumeResponse = await resumePurchase(branchId, id);
                        if (isCancelled) return;
                        purchase = resumeResponse?.purchase || purchase;
                    } catch (error) {
                        notify(error?.response?.data?.message || 'No se pudo reanudar la venta.');
                        navigate(`/branches/${branchId}/purchases`);
                        return;
                    }
                }
                setAddedOrder(purchase.order ? purchase.order : []);

                const isCredit = purchase.paymentTerm === 'credit' || purchase.paymentMethod === 'crédito';
                const isClosed =
                    purchase.isDeleted
                    || purchase.saleStatus === 'cancelled'
                    || purchase.saleStatus === 'completed'
                    || purchase.isPaid
                    || isCredit;
                const needsPaymentRedirect =
                    purchase.saleStatus === 'completed'
                    && !purchase.isDeleted
                    && purchase.paymentTerm !== 'credit'
                    && !purchase.paymentMethod
                    && !purchase.isPaid;
                if (needsPaymentRedirect) {
                    navigate(`/branches/${branchId}/purchases/${id}/receipt`);
                    return;
                }
                if (isClosed) {
                    notify('Esta venta está cerrada. No se puede editar.');
                    navigate(`/branches/${branchId}/purchases`);
                    return;
                }

                // Fill in purchase details
                setClientId(purchase.Client?.id);
                setName(purchase.Client?.name);
                setRut(purchase.Client?.rut || '');
                setEmail(purchase.Client?.email || '');
                setAddress(purchase.Client?.address || '');
                setCity(purchase.Client?.city || '');
                setPhone(purchase.Client?.phone || '');
                setGiro(purchase.Client?.giro || '');
                setClientType((prev) => prev || resolveSaleType('detalle'));
                setTypeSelection((prev) => prev || resolveSaleType('detalle'));
                setTotalPrice(purchase.totalAmount);
                setDocumentType(purchase.documentType || null);
                purchaseCreatedAtRef.current = purchase.createdAt || new Date().toISOString();
                setOrderDiscountAmount(
                    typeof purchase.orderDiscountAmount === 'number'
                        ? purchase.orderDiscountAmount
                        : null
                );
                setOrderDiscountPercentage(
                    typeof purchase.orderDiscountPercentage === 'number'
                        ? purchase.orderDiscountPercentage
                        : null
                );
                setOrderDiscountAuthorizationId(
                    Number.isInteger(purchase.orderDiscountAuthorizationId)
                        ? purchase.orderDiscountAuthorizationId
                        : null
                );
                setExcludedTaxIds(
                    Array.isArray(purchase.excludedTaxIds) ? purchase.excludedTaxIds : []
                );

                const purchaseItems = getPurchaseItems(purchase);
                const allVarieties = productsData.flatMap((product) => product.Varieties || []);
                const varietyById = new Map(allVarieties.map((variety) => [variety.id, variety]));
                const varietyByName = new Map();
                allVarieties.forEach((variety) => {
                    const key = variety?.varietyName ? String(variety.varietyName) : '';
                    if (!key || varietyByName.has(key)) return;
                    varietyByName.set(key, variety);
                });

                const findVariety = (item) => {
                    const byId = Number.isInteger(item?.varietyId) ? varietyById.get(item.varietyId) : null;
                    if (byId) return byId;
                    const key = item?.varietyName ? String(item.varietyName) : '';
                    return key ? varietyByName.get(key) || null : null;
                };

                const baseQuantities = buildEmptyMatrices(productsData);
                const baseDiscounts = buildEmptyMatrices(productsData);
                const baseAuthorizations = buildEmptyAuthorizationMatrices(productsData);
                const initialQuantities = { ...baseQuantities };
                const initialDiscounts = { ...baseDiscounts };
                const initialAuthorizations = { ...baseAuthorizations };
                const initialLinePriceMeta = {};

                const consolidatedBySlot = new Map();
                let consolidatedLines = 0;

                purchaseItems.forEach((item) => {
                    const variety = findVariety(item);
                    const priceIndex = resolveItemPriceIndex(variety, item);
                    if (!variety || priceIndex === -1) return;

                    const key = lineKey(variety.id, priceIndex);
                    const quantity = normalizeQuantity(item.quantity);
                    const discount = Number(item.discount || 0);
                    const authorizationId = Number.isInteger(item.discountAuthorizationId)
                        ? item.discountAuthorizationId
                        : null;
                    const defaultPriceType = normalizePriceTypeValue(
                        resolvePriceClientType(variety.VarietyPrices?.[priceIndex])
                    );
                    const nextPriceType = normalizePriceTypeValue(item?.priceType) || defaultPriceType;
                    const unitPrice = Number(variety.VarietyPrices?.[priceIndex]?.price ?? item?.price ?? 0) || 0;

                    if (!consolidatedBySlot.has(key)) {
                        consolidatedBySlot.set(key, {
                            varietyId: variety.id,
                            priceIndex,
                            quantity,
                            discount,
                            authorizationId,
                            priceType: nextPriceType,
                            priceTypeOverride: Boolean(item?.priceTypeOverride),
                            autoPriceSelection: Boolean(item?.autoPriceSelection),
                            unitPrice,
                        });
                        return;
                    }

                    consolidatedLines += 1;
                    const current = consolidatedBySlot.get(key);
                    const currentQuantity = normalizeQuantity(current.quantity || 0);
                    const nextQuantity = normalizeQuantity(currentQuantity + quantity);
                    const currentUnitPrice = Number(current.unitPrice || unitPrice) || 0;
                    const currentEffectiveSubtotal = (currentUnitPrice - Number(current.discount || 0)) * currentQuantity;
                    const incomingEffectiveSubtotal = (unitPrice - discount) * quantity;
                    const mergedEffectiveSubtotal = currentEffectiveSubtotal + incomingEffectiveSubtotal;
                    let mergedDiscount = 0;
                    if (nextQuantity > 0) {
                        const mergedEffectiveUnit = mergedEffectiveSubtotal / nextQuantity;
                        mergedDiscount = Math.round(currentUnitPrice - mergedEffectiveUnit);
                        if (!Number.isFinite(mergedDiscount)) mergedDiscount = 0;
                        mergedDiscount = Math.min(Math.max(mergedDiscount, 0), currentUnitPrice);
                    }

                    current.quantity = nextQuantity;
                    current.discount = mergedDiscount;
                    current.authorizationId =
                        Number.isInteger(current.authorizationId)
                        && Number.isInteger(authorizationId)
                        && Number(current.authorizationId) === Number(authorizationId)
                            ? current.authorizationId
                            : null;
                    current.priceType = current.priceType || nextPriceType;
                    current.priceTypeOverride = current.priceTypeOverride || Boolean(item?.priceTypeOverride);
                    current.autoPriceSelection = current.autoPriceSelection || Boolean(item?.autoPriceSelection);
                    consolidatedBySlot.set(key, current);
                });

                consolidatedBySlot.forEach((entry, key) => {
                    initialQuantities[entry.varietyId][entry.priceIndex] = entry.quantity;
                    initialDiscounts[entry.varietyId][entry.priceIndex] = entry.discount;
                    initialAuthorizations[entry.varietyId][entry.priceIndex] = entry.authorizationId;
                    initialLinePriceMeta[key] = {
                        priceType: entry.priceType,
                        priceTypeOverride: Boolean(entry.priceTypeOverride),
                        autoPriceSelection: Boolean(entry.autoPriceSelection),
                    };
                });

                if (consolidatedLines > 0 && !hasShownConsolidationWarningRef.current) {
                    hasShownConsolidationWarningRef.current = true;
                    notify(
                        'Se consolidaron líneas históricas duplicadas para esta venta. Revisa descuentos y cantidades antes de guardar.'
                    );
                }

                setDiscounts(initialDiscounts);
                setDiscountAuthorizations(initialAuthorizations);
                setQuantities(initialQuantities);
                setLinePriceMeta(initialLinePriceMeta);
                setIsEditHydrated(true);

                // Ensure subtotal/total reflect loaded data immediately
                calculateTotal(
                    initialQuantities,
                    initialDiscounts,
                    typeof purchase.orderDiscountAmount === 'number' ? purchase.orderDiscountAmount : null,
                    typeof purchase.orderDiscountPercentage === 'number' ? purchase.orderDiscountPercentage : null,
                    productsData
                );
            } catch (error) {
                if (isCancelled) return;
                console.log(error);
                notify('Error al cargar la información de la venta.');
                navigate(`/branches/${branchId}/purchases`);
            }
        };

        fetchData();
        return () => {
            isCancelled = true;
        };
    }, [branchId, confirm, id, isEditorPosView, navigate, resolveSaleType]);

    useEffect(() => {
        const fetchCategories = async () => {
            if (!branchId) return;
            setCategoriesLoading(true);
            setCategoriesError(null);
            try {
                const data = await getCategories({ includeAvailability: true, branchId });
                setCategories(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Error fetching categories:', error);
                setCategories([]);
                setCategoriesError('No se pudieron cargar las categorías.');
            } finally {
                setCategoriesLoading(false);
            }
        };
        fetchCategories();
    }, [branchId]);

    useEffect(() => {
        if (!branchId || !canExcludeTaxes) {
            setTaxes([]);
            setTaxesLoading(false);
            setTaxesError(null);
            setExcludedTaxIds([]);
            return;
        }
        let isMounted = true;
        const loadTaxes = async () => {
            setTaxesLoading(true);
            setTaxesError(null);
            try {
                const data = await getBranchTaxes(branchId);
                if (!isMounted) return;
                const normalized = Array.isArray(data) ? data : [];
                const active = normalized.filter(
                    (tax) => tax && tax.isActive !== false && tax.branchActive !== false
                );
                setTaxes(active);
                const activeIds = new Set(active.map((tax) => Number(tax.id)));
                setExcludedTaxIds((prev) =>
                    (prev || []).filter((id) => activeIds.has(Number(id)))
                );
            } catch (error) {
                if (!isMounted) return;
                console.error('Error fetching taxes:', error);
                setTaxes([]);
                setTaxesError('No se pudieron cargar los impuestos.');
            } finally {
                if (!isMounted) return;
                setTaxesLoading(false);
            }
        };
        loadTaxes();
        return () => {
            isMounted = false;
        };
    }, [branchId, canExcludeTaxes]);

    useEffect(() => {
        const fetchCashRegisters = async () => {
            if (!branchId) return;
            setPendingTabsScopeReady(false);
            try {
                const data = await getCashRegisters(branchId, { pageSize: 0 });
                const normalized = normalizePaginatedResponse(data);
                const registers = normalized.data || [];
                setCashRegisters(registers);

                const storedRegisterId = localStorage.getItem(
                    getPosRegisterStorageKey(branchId)
                );
                const storedSessionId = localStorage.getItem(
                    getPosSessionStorageKey(branchId)
                );
                const { selectedRegister, session } = resolveCurrentPosScope({
                    registers,
                    userId: user?.id,
                    storedRegisterId,
                    storedSessionId,
                });

                if (selectedRegister?.id != null) {
                    setCashRegisterId(selectedRegister.id);
                } else {
                    setCashRegisterId(null);
                }

                if (session?.id != null) {
                    setCashRegisterSessionId(session.id);
                } else {
                    setCashRegisterSessionId(null);
                }
            } catch (error) {
                console.error('Error fetching cash registers:', error);
                setCashRegisters([]);
                setCashRegisterId(null);
                setCashRegisterSessionId(null);
            } finally {
                setPendingTabsScopeReady(true);
            }
        };
        fetchCashRegisters();
    }, [branchId, user?.id]);

    useEffect(() => {
        if (selectedCategory === 'all') return;
        const isStillValid = displayCategories.some(
            (category) => String(category.id) === String(selectedCategory)
        );
        if (!isStillValid) {
            setSelectedCategory('all');
        }
    }, [displayCategories, selectedCategory]);

    useEffect(() => {
        if (!allowedClientTypes.length) return;
        if (!clientType) return;
        const resolvedType = resolveAllowedClientType(clientType);
        if (resolvedType !== clientType) {
            setClientType(resolvedType);
            setTypeSelection(resolvedType);
        }
    }, [allowedClientTypes, clientType, resolveAllowedClientType]);

    useEffect(() => {
        if (hasActiveSearch) {
            setShowMobileSearch(true);
        }
    }, [hasActiveSearch]);

    const toggleMobileSearch = () => {
        if (mobileSearchVisible && hasActiveSearch) return;
        setShowMobileSearch((prev) => {
            const next = !prev;
            if (next) {
                activateProductSearch();
                setTimeout(() => {
                    searchInputRef.current?.focus();
                    searchInputRef.current?.select();
                }, 0);
            }
            return next;
        });
    };

    const resolveCashRegisterPayload = () => {
        if (!cashRegisterId) {
            notify('Debes abrir una caja antes de actualizar la venta.');
            return null;
        }
        if (!cashRegisterSessionId || !isActiveSessionOwnedByUser) {
            if (!isActiveSessionOwnedByUser && activeSession) {
                notify('Caja abierta por otro usuario.');
            } else {
                notify('Debes abrir una caja antes de actualizar la venta.');
            }
            return null;
        }
        if (String(activeSession.id) !== String(cashRegisterSessionId)) {
            notify('La sesión activa de caja cambió. Recarga la página.');
            return null;
        }
        return { cashRegisterId, cashRegisterSessionId };
    };

    useEffect(() => {
        filterProducts(searchTerm, selectedCategory);
    }, [searchTerm, selectedCategory, availableProducts, clientType, resolveProductCategoryId, allowedClientTypes]);

    useEffect(() => {
        if (!isEditHydrated) return;
        setDataForPurchaseDetails();
    }, [
        quantities,
        discounts,
        orderDiscountAmount,
        orderDiscountPercentage,
        totalAmount,
        itemsSubtotal,
        linePriceMeta,
        addedOrder,
        isEditHydrated,
        documentType,
        name,
        rut,
        email,
        phone,
        address,
        city,
    ]);

    const setDataForPurchaseDetails = () => {
        const items = [];

        products.forEach(product => {
            product.Varieties.forEach(variety => {
                variety.VarietyPrices.forEach((price, index) => {
                    const quantity = normalizeQuantity(quantities[variety.id]?.[index] || 0);
                    if (!isPriceAllowedByRegister(price)) {
                        return;
                    }
                    if (quantity > 0) {
                        const meta = resolveLinePriceMeta(variety.id, index);
                        const canSelectPriceType = varietySupportsBarcodePriceTypeSelection(variety);
                        const resolvedPriceType = normalizePriceTypeValue(meta?.priceType)
                            || resolvePriceClientType(price);
                        items.push({
                            varietyId: variety.id,
                            varietyPriceId: price.id,
                            varietyName: variety.varietyName,
                            productName: product.productName,
                            metricUnit: price.metricUnit,
                            price: price.price,
                            discount: discounts?.[variety.id]?.[index] || 0,
                            quantity,
                            priceIndex: index,
                            priceType: resolvedPriceType,
                            priceTypeOverride: canSelectPriceType && Boolean(meta?.priceTypeOverride),
                            ...(canSelectPriceType && meta?.autoPriceSelection ? { autoPriceSelection: true } : {}),
                        });
                    }
                });
            });
        });

        const orderedItems = orderAndIndexLineItems(items);
        const nextPurchaseDetails = {
            Client: { name, rut, email, phone, address, city },
            purchaseItems: orderedItems,
            itemsSubtotal,
            orderDiscountAmount,
            orderDiscountPercentage,
            totalAmount,
            id,
            documentType,
            createdAt: purchaseCreatedAtRef.current || new Date().toISOString(),
        };

        setPurchaseDetails(nextPurchaseDetails);
        return nextPurchaseDetails;
    };

    const handleDiscountChange = async (varietyId, priceIndex, discount) => {
        if (!canEditSale || !canSell) return;
        const parsedValue = parseInt(discount);
        const newDiscount = isNaN(parsedValue) || parsedValue < 0 ? 0 : parsedValue;

        if (newDiscount > 0) {
            const variety = products
                .flatMap((product) => product.Varieties || [])
                .find((item) => item.id === varietyId);
            const label = variety?.varietyName || null;
            const opened = openDiscountAuthorization({
                type: 'item',
                varietyId,
                priceIndex,
                discountAmount: newDiscount,
                discountPercent: null,
                label,
            });
            if (opened) return;
        }

        applyItemDiscount(varietyId, priceIndex, newDiscount);
    };

    const applyItemDiscount = (varietyId, priceIndex, newDiscount, authorizationId = null) => {
        setDiscounts((prevDiscounts) => {
            const newDiscounts = { ...prevDiscounts };
            if (!newDiscounts[varietyId]) newDiscounts[varietyId] = [];
            newDiscounts[varietyId][priceIndex] = newDiscount;

            setDiscountAuthorizations((prevAuthorizations) => {
                const nextAuthorizations = { ...prevAuthorizations };
                if (!nextAuthorizations[varietyId]) nextAuthorizations[varietyId] = [];
                nextAuthorizations[varietyId][priceIndex] = newDiscount > 0 ? authorizationId : null;
                const total = calculateTotal(quantities, newDiscounts);

                handleSave(false, quantities, total, newDiscounts, null, null, nextAuthorizations, orderDiscountAuthorizationId);
                return nextAuthorizations;
            });

            return newDiscounts;
        });
    };

    const getPendingDiscountKey = (payload) =>
        payload?.type === 'item'
            ? `${payload.varietyId}-${payload.priceIndex}`
            : 'order';

    const openDiscountAuthorization = (payload) => {
        if (!canEditSale || !canSell) return false;
        if (!cashRegisterId) {
            notify('Debes seleccionar una caja antes de aplicar descuentos.');
            return false;
        }
        if (isAdmin) return false;
        setPendingDiscountAuthorization(payload);
        setDiscountAuthorizationInitialState(null);
        setShowDiscountAuthorizationModal(true);
        return true;
    };

    const handleDiscountRequested = (response) => {
        if (!pendingDiscountAuthorization) return;
        const entry = {
            ...pendingDiscountAuthorization,
            requested: true,
            expiresAt: response?.expiresAt || null,
            attemptsRemaining:
                typeof response?.attemptsRemaining === 'number'
                    ? response.attemptsRemaining
                    : null,
        };
        if (entry.type === 'item') {
            const key = getPendingDiscountKey(entry);
            setPendingDiscountRequests((prev) => ({
                ...prev,
                items: { ...prev.items, [key]: entry },
            }));
        } else {
            setPendingDiscountRequests((prev) => ({ ...prev, order: entry }));
        }
    };

    const clearPendingDiscount = (payload) => {
        if (!payload) return;
        if (payload.type === 'item') {
            const key = getPendingDiscountKey(payload);
            setPendingDiscountRequests((prev) => {
                if (!prev.items?.[key]) return prev;
                const nextItems = { ...prev.items };
                delete nextItems[key];
                return { ...prev, items: nextItems };
            });
        } else {
            setPendingDiscountRequests((prev) => ({ ...prev, order: null }));
        }
    };

    const resumeDiscountAuthorization = (payload) => {
        if (!payload) return;
        setPendingDiscountAuthorization(payload);
        setDiscountAuthorizationInitialState({
            requested: true,
            expiresAt: payload.expiresAt || null,
            attemptsRemaining:
                typeof payload.attemptsRemaining === 'number' ? payload.attemptsRemaining : null,
        });
        setShowDiscountAuthorizationModal(true);
    };

    const handleOrderDiscountApply = (amount, percentage) => {
        if (!canEditSale || !canSell) return;
        const discountAmount = (() => {
            if (typeof percentage === 'number' && percentage > 0) {
                return Math.floor((itemsSubtotal || 0) * (percentage / 100));
            }
            if (typeof amount === 'number') return amount;
            return 0;
        })();
        if (discountAmount > 0) {
            const opened = openDiscountAuthorization({
                type: 'order',
                discountAmount,
                discountPercent: typeof percentage === 'number' ? percentage : null,
                orderAmount: amount,
                orderPercent: percentage,
                label: 'Descuento de orden',
            });
            if (opened) return;
        }
        applyOrderDiscount(amount, percentage);
    };

    const applyOrderDiscount = (amount, percentage, authorizationId = null) => {
        const hasDiscount = (typeof percentage === 'number' && percentage > 0)
            || (typeof amount === 'number' && amount > 0);
        const nextAuthorizationId = hasDiscount ? authorizationId : null;
        setOrderDiscountAmount(percentage != null ? null : amount);
        setOrderDiscountPercentage(percentage != null ? percentage : null);
        setOrderDiscountAuthorizationId(nextAuthorizationId);
        const total = calculateTotal(quantities, discounts, amount, percentage);
        handleSave(
            false,
            quantities,
            total,
            discounts,
            null,
            null,
            discountAuthorizations,
            nextAuthorizationId,
            amount,
            percentage
        );
    };

    const openPurchaseModal = () => {
        setDataForPurchaseDetails();
        setShowPurchaseModal(true);
        setModalProduct(null);
        setModalVariety(null);
        setModalVarietyPrice(null);
        setSlotModalState(null);
    };
    usePosNavbarCart({ enabled: isEditorPosView, totalAmount, onOpen: openPurchaseModal });

    const handleQuantityChange = (varietyId, priceIndex, value) => {
        if (!canEditSale) return;
        const quantity = normalizeQuantity(value);
        if (quantity > MAX_PURCHASE_ITEM_QUANTITY) {
            notify(`La cantidad excede el máximo permitido (${MAX_PURCHASE_ITEM_QUANTITY}).`);
            return;
        }

        const variety = products.flatMap((product) => product.Varieties || []).find((item) => item.id === varietyId);
        const price = variety?.VarietyPrices?.[priceIndex];
        if (!isPriceAllowedByRegister(price)) {
            return;
        }

        setQuantities((prevQuantities) => {
            const newQuantities = { ...prevQuantities };
            if (!newQuantities[varietyId]) newQuantities[varietyId] = [];
            newQuantities[varietyId][priceIndex] = quantity;

            // Update order
            setAddedOrder(prev => {
                const exists = prev.some(i => i.varietyId === varietyId && i.priceIndex === priceIndex);
                if (quantity > 0 && !exists) {
                    return [{ varietyId, priceIndex }, ...prev];
                } else if (quantity === 0 && exists) {
                    return prev.filter(i => !(i.varietyId === varietyId && i.priceIndex === priceIndex));
                }
                return prev;
            });

            const total = calculateTotal(newQuantities);
            handleSave(
                false,
                newQuantities,
                total,
                null,
                null,
                null,
                discountAuthorizations,
                orderDiscountAuthorizationId
            );
            return newQuantities;
        });
    };

    const handleQuantityIncrement = (varietyId, priceIndex, increment) => {
        if (!canEditSale) return;
        const variety = products.flatMap((product) => product.Varieties || []).find((item) => item.id === varietyId);
        const price = variety?.VarietyPrices?.[priceIndex];
        if (!isPriceAllowedByRegister(price)) {
            return;
        }

        setQuantities((prevQuantities) => {
            const newQuantities = { ...prevQuantities };
            newQuantities[varietyId] = [...(newQuantities[varietyId] || [])];
            const newQty = normalizeQuantity(
                Math.max(0, normalizeQuantity(newQuantities[varietyId][priceIndex] || 0) + increment)
            );
            if (newQty > MAX_PURCHASE_ITEM_QUANTITY) {
                notify(`La cantidad excede el máximo permitido (${MAX_PURCHASE_ITEM_QUANTITY}).`);
                return prevQuantities;
            }
            newQuantities[varietyId][priceIndex] = newQty;

            // Update order
            setAddedOrder(prev => {
                const exists = prev.some(i => i.varietyId === varietyId && i.priceIndex === priceIndex);
                if (newQty > 0 && !exists) {
                    return [{ varietyId, priceIndex }, ...prev];
                } else if (newQty === 0 && exists) {
                    return prev.filter(i => !(i.varietyId === varietyId && i.priceIndex === priceIndex));
                }
                return prev;
            });

            const total = calculateTotal(newQuantities);
            handleSave(
                false,
                newQuantities,
                total,
                null,
                null,
                null,
                discountAuthorizations,
                orderDiscountAuthorizationId
            );
            return newQuantities;
        });
    };

    const resolveModalPriceIndex = useCallback(() => {
        if (!modalVariety?.VarietyPrices?.length || !modalVarietyPrice) return -1;
        return modalVariety.VarietyPrices.findIndex((entry) => (
            modalVarietyPrice?.id != null ? entry?.id === modalVarietyPrice.id : entry === modalVarietyPrice
        ));
    }, [modalVariety, modalVarietyPrice]);

    const isScaleInputPrice = useCallback((price) => (
        Boolean(price?.useScaleWeight && isScaleWeightMetricUnit(price?.metricUnit))
    ), []);

    const buildActiveScaleSelection = useCallback((variety, price, priceIndex) => ({
        variety,
        price,
        priceIndex,
        varietyId: variety?.id,
        varietyPriceId: price?.id,
    }), []);

    const isSameActiveScaleSelection = useCallback((selection, varietyId, priceIndex, price) => {
        if (!selection) return false;
        if (Number(selection.varietyId) !== Number(varietyId)) return false;
        if (Number.isInteger(selection.priceIndex) && Number(selection.priceIndex) === Number(priceIndex)) {
            return true;
        }
        return selection.varietyPriceId != null && Number(selection.varietyPriceId) === Number(price?.id);
    }, []);

    const getLatestScaleWeightKg = useCallback(() => {
        if (!scaleState?.hasLiveScaleStream) return null;
        const rawWeightKg = scaleState?.freshStableWeightKg
            ?? scaleState?.latestStableWeightKg
            ?? lastStableScaleReading?.weightKg;
        const weightKg = Number(rawWeightKg);
        return rawWeightKg != null && Number.isFinite(weightKg) && weightKg >= 0 ? weightKg : null;
    }, [
        lastStableScaleReading?.weightKg,
        scaleState?.freshStableWeightKg,
        scaleState?.latestStableWeightKg,
        scaleState?.hasLiveScaleStream,
    ]);

    const openSlotConfirmModal = useCallback((slotEntry, options = {}) => {
        if (!slotEntry?.variety || !slotEntry?.price || slotEntry?.disabled) return;
        const quantityMode = options?.quantityMode === 'set' ? 'set' : 'add';
        const currentQuantity = normalizeQuantity(
            quantities?.[slotEntry.varietyId]?.[slotEntry.priceIndex] || 0
        );
        applyBarcodeAutoPriceSelectionMeta(slotEntry.variety, slotEntry.priceIndex);
        setShowClientModal(false);
        setShowPurchaseModal(false);
        setModalProduct(null);
        setModalVariety(slotEntry.variety);
        setModalVarietyPrice(slotEntry.price);
        setSlotModalState({
            initialQuantity: quantityMode === 'set' ? currentQuantity : 0,
            baseQuantity: currentQuantity,
            source: 'slot',
            quantityMode,
        });
    }, [applyBarcodeAutoPriceSelectionMeta, quantities]);

    const handleConfirmSlotQuantity = useCallback((nextRawQuantity) => {
        const resolvedPriceIndex = resolveModalPriceIndex();
        if (!modalVariety || resolvedPriceIndex < 0) return false;
        const currentQty = normalizeQuantity(
            slotModalState?.baseQuantity ?? quantities?.[modalVariety.id]?.[resolvedPriceIndex] ?? 0
        );
        const nextQty = slotModalState?.quantityMode === 'set'
            ? normalizeQuantity(nextRawQuantity)
            : normalizeQuantity(currentQty + nextRawQuantity);
        if (nextQty > MAX_PURCHASE_ITEM_QUANTITY) {
            handleQuantityChange(modalVariety.id, resolvedPriceIndex, nextQty);
            return false;
        }
        handleQuantityChange(modalVariety.id, resolvedPriceIndex, nextQty);
        setSlotModalState(null);
        return true;
    }, [
        handleQuantityChange,
        modalVariety,
        quantities,
        resolveModalPriceIndex,
        slotModalState?.baseQuantity,
        slotModalState?.quantityMode,
    ]);

    const handleModalQuantityChange = useCallback((varietyId, priceIndex, value) => {
        if (!slotModalState || !modalVariety || Number(modalVariety.id) !== Number(varietyId)) {
            return handleQuantityChange(varietyId, priceIndex, value);
        }

        const rawQuantity = normalizeQuantity(value);
        const baseQuantity = normalizeQuantity(
            slotModalState?.baseQuantity ?? quantities?.[varietyId]?.[priceIndex] ?? 0
        );
        const nextQty = slotModalState?.quantityMode === 'set'
            ? rawQuantity
            : normalizeQuantity(baseQuantity + rawQuantity);
        if (nextQty > MAX_PURCHASE_ITEM_QUANTITY) {
            return handleQuantityChange(varietyId, priceIndex, nextQty);
        }
        return handleQuantityChange(varietyId, priceIndex, nextQty);
    }, [
        handleQuantityChange,
        modalVariety,
        quantities,
        slotModalState,
    ]);

    const handleAddPosSlot = useCallback(() => {
        if (!posSlotLayoutScopeKey) {
            notify('Selecciona una caja activa antes de agregar favs.');
            return;
        }
        setShowSlotPickerModal(true);
    }, [posSlotLayoutScopeKey]);

    const handleSlotAddSelection = useCallback(async (entry) => {
        try {
            await saveCurrentPosSlotLayout([
                ...currentPosSlotLayout,
                {
                    productId: entry?.productId,
                },
            ]);
            setShowSlotPickerModal(false);
        } catch (error) {
            notify(error?.message || 'No se pudo guardar el fav.');
        }
    }, [currentPosSlotLayout, saveCurrentPosSlotLayout]);

    const handleFavoriteProductSelect = useCallback((slotEntry) => {
        if (slotEntry?.disabled || !slotEntry?.product) return;
        setModalProduct(slotEntry.product);
    }, []);

    const handlePriceCardIncrement = useCallback((price, priceIndex) => {
        if (!modalVariety || !isPriceAllowedByRegister(price)) return;
        applyBarcodeAutoPriceSelectionMeta(modalVariety, priceIndex);
        if (!isScaleInputPrice(price)) {
            handleQuantityIncrement(modalVariety.id, priceIndex, 1);
            return;
        }

        if (!scaleState?.hasLiveScaleStream) {
            openSlotConfirmModal({
                variety: modalVariety,
                price,
                priceIndex,
                varietyId: modalVariety.id,
                varietyPriceId: price?.id,
            }, { quantityMode: 'set' });
            return;
        }

        setActiveScaleSelection(buildActiveScaleSelection(modalVariety, price, priceIndex));
        const weightKg = getLatestScaleWeightKg();
        if (weightKg == null) return;

        const nextQty = normalizeQuantity(weightKg);
        if (nextQty > MAX_PURCHASE_ITEM_QUANTITY) {
            notify(`La cantidad excede el máximo permitido (${MAX_PURCHASE_ITEM_QUANTITY}).`);
            return;
        }
        handleQuantityChange(modalVariety.id, priceIndex, nextQty);
    }, [
        applyBarcodeAutoPriceSelectionMeta,
        buildActiveScaleSelection,
        getLatestScaleWeightKg,
        handleQuantityChange,
        handleQuantityIncrement,
        isPriceAllowedByRegister,
        isScaleInputPrice,
        modalVariety,
        openSlotConfirmModal,
        scaleState?.hasLiveScaleStream,
    ]);

    const handlePriceCardDecrement = useCallback((price, priceIndex) => {
        if (!modalVariety || !isPriceAllowedByRegister(price)) return;
        applyBarcodeAutoPriceSelectionMeta(modalVariety, priceIndex);
        if (!isScaleInputPrice(price)) {
            handleQuantityIncrement(modalVariety.id, priceIndex, -1);
            return;
        }

        if (isSameActiveScaleSelection(activeScaleSelection, modalVariety.id, priceIndex, price)) {
            setActiveScaleSelection(null);
        }
        handleQuantityChange(modalVariety.id, priceIndex, 0);
    }, [
        applyBarcodeAutoPriceSelectionMeta,
        activeScaleSelection,
        handleQuantityChange,
        handleQuantityIncrement,
        isPriceAllowedByRegister,
        isSameActiveScaleSelection,
        isScaleInputPrice,
        modalVariety,
    ]);

    const handlePriceCardKeypad = useCallback((price, priceIndex) => {
        if (!modalVariety || !isPriceAllowedByRegister(price)) return;
        if (isScaleInputPrice(price)) {
            setActiveScaleSelection(null);
        }
        openSlotConfirmModal({
            variety: modalVariety,
            price,
            priceIndex,
            varietyId: modalVariety.id,
            varietyPriceId: price?.id,
        }, { quantityMode: 'set' });
    }, [isPriceAllowedByRegister, isScaleInputPrice, modalVariety, openSlotConfirmModal]);

    const handleFinishPurchase = async () => {
        if (!canEditSale) return;
        setPendingDocumentType(null);
        setShowDocumentTypeModal(true);
    };

    const handleSuspendSale = async () => {
        if (!canUpdateSale) return;
        const hasItems = Object.values(quantities || {}).some(
            (list) =>
                Array.isArray(list)
                && list.some((qty) => normalizeQuantity(qty) > 0)
        );
        if (!hasItems) {
            notify('Agrega productos antes de suspender la venta.');
            return;
        }
        const confirmed = await confirm({
            title: 'Suspender venta',
            message: '¿Suspender esta venta? Se generara un voucher con codigo de barras.',
            confirmLabel: 'Suspender',
            cancelLabel: 'Cancelar',
            tone: 'warning',
        });
        if (!confirmed) return;
        try {
            const saved = await handleSave(false);
            if (!saved) return;
            await suspendPurchase(branchId, id);
            navigate(`/branches/${branchId}/purchases/${id}/receipt`);
        } catch (error) {
            console.error('Error suspending sale:', error);
            notify(error?.response?.data?.message || 'No se pudo suspender la venta.');
        }
    };

    const handleDocumentTypeSelect = async (type) => {
        if (!canEditSale) return;
        setPendingDocumentType(type);
        if (type === 'quotation') {
            if (!canConvertToQuotation) return;
            setShowDocumentTypeModal(false);
            const confirmed = await confirm({
                title: 'Convertir a cotización',
                message: '¿Convertir esta venta a cotización? La venta será eliminada.',
                confirmLabel: 'Convertir',
                cancelLabel: 'Cancelar',
                tone: 'warning',
            });
            if (!confirmed) {
                setPendingDocumentType(null);
                return;
            }
            await handleConvertToQuotation();
            setPendingDocumentType(null);
            return;
        }

        const confirmMsg = type === 'boleta'
            ? 'Finalizar como Boleta (cliente opcional)?'
            : 'Finalizar como Factura (requiere cliente). ¿Continuar?';
        const confirmed = await confirm({
            title: 'Finalizar venta',
            message: confirmMsg,
            confirmLabel: 'Continuar',
            cancelLabel: 'Cancelar',
            tone: 'warning',
        });
        if (!confirmed) {
            setPendingDocumentType(null);
            return;
        }

        setDocumentType(type);
        setShowDocumentTypeModal(false);

        if (type === 'boleta') {
            await handleSave(
                true,
                null,
                null,
                null,
                clientId || null,
                'boleta',
                discountAuthorizations,
                orderDiscountAuthorizationId
            );
            setPendingDocumentType(null);
            return;
        }

        if (clientId) {
            await handleSave(
                true,
                null,
                null,
                null,
                clientId,
                'factura',
                discountAuthorizations,
                orderDiscountAuthorizationId
            );
            setPendingDocumentType(null);
            return;
        }

        setPendingFinishAfterClient(true);
        setShowClientModal(true);
    };

    const savePurchaseClient = async (selectedClientId) => {
        if (!canUpdateSale) return;
        const cashRegisterPayload = resolveCashRegisterPayload();
        if (!cashRegisterPayload) return;
        try {
            await updatePurchase(branchId, id, {
                clientId: selectedClientId,
                documentType,
                ...cashRegisterPayload,
                ...(canExcludeTaxes ? { excludedTaxIds } : {}),
            });
        } catch (error) {
            console.error('Error actualizando cliente en la venta.', error);
        }
    };

    const handleClientSelected = async (client, options = {}) => {
        const selectionSource = options?.source || 'manual';
        if (client) {
            setClientId(client.id);
            setName(client.name || '');
            setRut(client.rut || '');
            setEmail(client.email || '');
            setAddress(client.address || '');
            setCity(client.city || '');
            setPhone(client.phone || '');
            setGiro(client.giro || '');
        }
        if (pendingConversionAfterClient) {
            if (selectionSource === 'auto' || selectionSource === 'auto-clear') {
                return;
            }
            const selectedClientId = client?.id || clientId;
            if (!selectedClientId) return;
            setPendingConversionAfterClient(false);
            setPendingDocumentType(null);
            await handleConvertToQuotation(selectedClientId);
            return;
        }
        if (pendingFinishAfterClient && pendingDocumentType === 'factura') {
            const selectedClientId = client?.id || clientId;
            if (!selectedClientId) return;
            setPendingFinishAfterClient(false);
            setPendingDocumentType(null);
            await handleSave(
                true,
                null,
                null,
                null,
                selectedClientId,
                'factura',
                discountAuthorizations,
                orderDiscountAuthorizationId
            );
        }
    };

    const handleCloseClientModal = (open) => {
        setShowClientModal(open);
        if (!open) {
            setPendingFinishAfterClient(false);
            setPendingConversionAfterClient(false);
            setPendingDocumentType(null);
        }
    };

    const handleCloseDocumentTypeModal = (open) => {
        setShowDocumentTypeModal(open);
        if (!open) {
            setPendingDocumentType(null);
            setPendingFinishAfterClient(false);
            setPendingConversionAfterClient(false);
        }
    };

    const handleCloseTypeModal = () => {
        setShowTypeModal(false);
    };

    const handleOpenClientModal = () => {
        if (!canReadClient && !canCreateClient) {
            notify('No tienes permisos para seleccionar clientes.');
            return;
        }
        setShowClientModal(true);
    };

    const calculateTotal = (
        quantitiesData,
        discountsData,
        overrideOrderDiscountAmount,
        overrideOrderDiscountPercentage,
        productsOverride
    ) => {
        const totals = computeTotals(
            Array.isArray(productsOverride) ? productsOverride : products,
            quantitiesData,
            discountsData,
            overrideOrderDiscountAmount,
            overrideOrderDiscountPercentage
        );
        setItemsSubtotal(totals.subtotal);
        setTotalPrice(totals.total);
        return totals.total;
    };

    const buildPurchaseItemsPayload = ({
        quantitiesData = quantities,
        discountsData = discounts,
        discountAuthorizationsData = discountAuthorizations,
        linePriceMetaData = linePriceMeta,
    } = {}) => {
        const rawItems = products.reduce((acc, product) => {
            const productItems = product.Varieties.flatMap((variety) =>
                (variety.VarietyPrices || []).map((price, index) => {
                    if (!isPriceAllowedByRegister(price)) return null;
                    const quantity = normalizeQuantity(quantitiesData?.[variety.id]?.[index] || 0);
                    if (quantity <= 0) return null;
                    const meta = linePriceMetaData[lineKey(variety.id, index)] || null;
                    const canSelectPriceType = varietySupportsBarcodePriceTypeSelection(variety);
                    const resolvedPriceType = normalizePriceTypeValue(meta?.priceType)
                        || resolvePriceClientType(price);
                    return {
                        varietyId: variety.id,
                        varietyPriceId: price.id,
                        varietyName: variety.varietyName,
                        productName: product.productName,
                        quantity,
                        discount: discountsData?.[variety.id]?.[index] || 0,
                        discountAuthorizationId: discountAuthorizationsData?.[variety.id]?.[index] ?? null,
                        price: price.price,
                        metricUnit: price.metricUnit,
                        priceIndex: index,
                        priceType: resolvedPriceType,
                        priceTypeOverride: canSelectPriceType && Boolean(meta?.priceTypeOverride),
                        ...(canSelectPriceType && meta?.autoPriceSelection ? { autoPriceSelection: true } : {}),
                    };
                }).filter(Boolean)
            );
            return acc.concat(productItems);
        }, []);
        return orderAndIndexLineItems(rawItems);
    };

    const handleSave = async (
        isPaid,
        newQuantities,
        total,
        newDiscounts,
        overrideClientId,
        overrideDocumentType,
        discountAuthorizationsOverride,
        orderDiscountAuthorizationOverride,
        orderDiscountAmountOverride,
        orderDiscountPercentageOverride,
        linePriceMetaOverride,
    ) => {
        if (!canUpdateSale) return;
        const cashRegisterPayload = resolveCashRegisterPayload();
        if (!cashRegisterPayload) return false;
        const resolvedDiscounts = newDiscounts || discounts;
        const resolvedDiscountAuthorizations = discountAuthorizationsOverride || discountAuthorizations;
        const resolvedOrderDiscountAmount = orderDiscountAmountOverride !== undefined
            ? orderDiscountAmountOverride
            : orderDiscountAmount;
        const resolvedOrderDiscountPercentage = orderDiscountPercentageOverride !== undefined
            ? orderDiscountPercentageOverride
            : orderDiscountPercentage;
        const resolvedOrderDiscountAuthorizationId = orderDiscountAuthorizationOverride !== undefined
            ? orderDiscountAuthorizationOverride
            : orderDiscountAuthorizationId;
        const resolvedQuantities = { ...quantities, ...(newQuantities || {}) };
        const computedTotals = computeTotals(
            products,
            resolvedQuantities,
            resolvedDiscounts,
            resolvedOrderDiscountAmount,
            resolvedOrderDiscountPercentage
        );
        const parsedTotal = Number(total);
        const resolvedTotalAmount = Number.isFinite(parsedTotal)
            ? parsedTotal
            : computedTotals.total;
        const items = buildPurchaseItemsPayload({
            quantitiesData: resolvedQuantities,
            discountsData: resolvedDiscounts,
            discountAuthorizationsData: resolvedDiscountAuthorizations,
            linePriceMetaData: linePriceMetaOverride || linePriceMeta,
        });
        const invalidQuantityItem = findInvalidPurchaseItemQuantity(items);
        if (invalidQuantityItem) {
            notify(buildPurchaseItemQuantityLimitMessage(invalidQuantityItem));
            return false;
        }

        const resolvedClientId = overrideClientId !== undefined ? overrideClientId : clientId;
        const resolvedDocumentType = overrideDocumentType !== undefined ? overrideDocumentType : documentType;

        try {
            await updatePurchase(branchId, id, {
                purchaseItems: items,
                totalAmount: resolvedTotalAmount,
                clientId: resolvedClientId,
                order: addedOrder,
                orderDiscountAmount: resolvedOrderDiscountAmount,
                orderDiscountPercentage: resolvedOrderDiscountPercentage,
                orderDiscountAuthorizationId: resolvedOrderDiscountAuthorizationId,
                documentType: resolvedDocumentType,
                ...cashRegisterPayload,
                ...(canExcludeTaxes ? { excludedTaxIds } : {}),
            });
            if (isPaid) {
                navigate(`/branches/${branchId}/purchases/${id}/receipt`);
            }
            return true;
        } catch (error) {
            console.log(error);
            notify('Error al actualizar la venta.');
            return false;
        }
    };

    const handleConvertToQuotation = async (overrideClientId) => {
        if (!canConvertToQuotation) return;
        const resolvedClientId = overrideClientId ?? clientId;
        if (!resolvedClientId) {
            setPendingConversionAfterClient(true);
            setShowClientModal(true);
            return;
        }
        try {
            const response = await convertPurchaseToQuotation(branchId, id, {
                clientId: resolvedClientId,
            });
            if (response?.quotation?.id) {
                navigate(`/branches/${branchId}/quotations/${response.quotation.id}`);
            } else {
                navigate(`/branches/${branchId}/quotations`);
            }
        } catch (error) {
            const data = error?.response?.data;
            if (data?.issues?.length) {
                const details = data.issues
                    .map((issue) => issue.varietyName
                        ? `${issue.varietyName}: ${issue.reason}`
                        : issue.reason)
                    .join('\n');
                notify(`${data.message || 'No se pudo convertir la venta.'}\n${details}`);
                return;
            }
            notify(data?.message || error.message || 'No se pudo convertir la venta.');
        }
    };

    const handleClientTypeChange = async (newType) => {
        if (!newType) {
            setClientType('');
            setTypeSelection('');
            return;
        }
        const resolvedType = resolveAllowedClientType(newType);
        if (!resolvedType) return;
        setClientType(resolvedType);
        setTypeSelection(resolvedType);
    };

    const orderLabel = documentType === 'factura'
        ? 'Factura'
        : documentType === 'boleta'
            ? 'Boleta'
            : 'Orden';

    const cartWrapperClassName = 'hidden md:flex md:flex-col md:justify-end fixed top-[220px] bottom-0 left-0 w-[max(440px,33.333333%)] px-4 z-0';

    const cartPanelClassName = 'bg-white shadow-lg rounded-lg p-4 md:overflow-y-auto md:max-h-[calc(86vh-260px)] md:h-[calc(86vh-260px)]';

    const mainContentClassName = 'flex-1 md:pl-[max(440px,33.333333%)] p-4 overflow-auto';

    const findVarietyForItem = (item) =>
        products.flatMap((product) => product.Varieties).find((variety) => variety.id === item.varietyId);

    const resolveItemPriceIndex = (variety, item) => {
        if (!variety?.VarietyPrices?.length || !item) return -1;
        if (Number.isInteger(item.priceIndex)) return item.priceIndex;
        if (Number.isInteger(item.varietyPriceId)) {
            const byId = variety.VarietyPrices.findIndex((price) => price.id === item.varietyPriceId);
            if (byId !== -1) return byId;
        }
        const exact = variety.VarietyPrices.findIndex(
            (price) => price.price === item.price && price.metricUnit === item.metricUnit
        );
        if (exact !== -1) return exact;
        if (item.metricUnit) {
            const byMetric = variety.VarietyPrices.findIndex((price) => price.metricUnit === item.metricUnit);
            if (byMetric !== -1) return byMetric;
        }
        if (item.price != null) {
            const byPrice = variety.VarietyPrices.findIndex((price) => price.price === item.price);
            if (byPrice !== -1) return byPrice;
        }
        return -1;
    };

    const cartItems = useMemo(() => {
        const baseItems = getPurchaseItems(purchaseDetails);
        if (!baseItems.length) return [];
        return baseItems.map((item) => {
            if (Number.isInteger(item.priceIndex)) return item;
            const resolvedIndex = resolveItemPriceIndex(findVarietyForItem(item), item);
            if (Number.isInteger(resolvedIndex) && resolvedIndex >= 0) {
                return { ...item, priceIndex: resolvedIndex };
            }
            return item;
        });
    }, [purchaseDetails, products]);

    const getItemQuantity = (item) => {
        const resolvedIndex = Number.isInteger(item.priceIndex)
            ? item.priceIndex
            : resolveItemPriceIndex(findVarietyForItem(item), item);
        if (!Number.isInteger(resolvedIndex) || resolvedIndex < 0) {
            return normalizeQuantity(item.quantity ?? 0);
        }
        return normalizeQuantity(quantities[item.varietyId]?.[resolvedIndex] || 0);
    };

    const getItemPriceTypeOptions = (item, variety) => {
        if (!variety) return [];
        if (!varietySupportsBarcodePriceTypeSelection(variety)) return [];
        const seen = new Set();
        const options = [];
        (variety.VarietyPrices || []).forEach((price) => {
            if (!isPriceAllowedByRegister(price)) return;
            const type = normalizePriceTypeValue(resolvePriceClientType(price));
            if (!type || seen.has(type)) return;
            seen.add(type);
            options.push({ value: type, label: getPriceTypeLabel(type) });
        });
        return options.length > 1 ? options : [];
    };

    const editorActivePurchaseId =
        isEditHydrated && Number.isFinite(activePurchaseNumericId)
            ? activePurchaseNumericId
            : null;
    const resolveProductNameForVariety = useCallback((varietyId) => {
        const matchingProduct = products.find((product) =>
            (product.Varieties || []).some((entry) => Number(entry?.id) === Number(varietyId))
        );
        return matchingProduct?.productName || null;
    }, [products]);
    const activeScaleWeighing = useMemo(() => {
        const activeVariety = activeScaleSelection?.variety;
        const activePrice = activeScaleSelection?.price;
        if (!isEditorPosView || !activeVariety || !activePrice) return null;
        if (!activePrice?.useScaleWeight) return null;
        if (!isScaleWeightMetricUnit(activePrice?.metricUnit)) return null;
        const resolvedPriceIndex = Number.isInteger(activeScaleSelection?.priceIndex)
            ? activeScaleSelection.priceIndex
            : (activeVariety?.VarietyPrices || []).findIndex(
                (entry) => Number(entry?.id) === Number(activePrice?.id)
            );
        const unitDiscount = resolvedPriceIndex >= 0
            ? Number(discounts?.[activeVariety?.id]?.[resolvedPriceIndex] || 0)
            : 0;
        const display = resolveVarietyPriceDisplay(activeVariety, activePrice, unitDiscount);
        return {
            activePurchaseId: editorActivePurchaseId,
            varietyId: activeVariety?.id,
            varietyPriceId: activePrice?.id,
            productName: resolveProductNameForVariety(activeVariety?.id),
            varietyName: activeVariety?.varietyName || null,
            metricUnit: activePrice?.metricUnit || null,
            unitPrice: Number.isFinite(Number(display?.price)) ? Number(display.price) : null,
        };
    }, [
        activeScaleSelection,
        discounts,
        editorActivePurchaseId,
        isEditorPosView,
        resolveProductNameForVariety,
        resolveVarietyPriceDisplay,
    ]);
    const helperActiveSaleLabel = helperSharedPurchase?.id
        ? `Venta activa: #${helperSharedPurchase.id}`
        : helperSharedPurchase
            ? 'Venta nueva en curso'
            : posSyncHasEditor
                ? 'Esperando venta activa'
                : 'Esperando editor POS';
    useEffect(() => {
        if (!posSyncEnabled || !isEditorPosView) return;
        publishActivePurchaseId(editorActivePurchaseId);
    }, [
        editorActivePurchaseId,
        isEditorPosView,
        posSyncConnectionStatus,
        posSyncEnabled,
        publishActivePurchaseId,
    ]);

    useEffect(() => {
        if (!posSyncEnabled || !isEditorPosView) return;
        publishSharedPreview(purchaseDetails);
    }, [
        isEditorPosView,
        posSyncConnectionStatus,
        posSyncEnabled,
        publishSharedPreview,
        purchaseDetails,
    ]);

    useEffect(() => {
        if (!posSyncEnabled || !isEditorPosView) return;
        publishSharedWeighing(activeScaleWeighing);
    }, [
        activeScaleWeighing,
        isEditorPosView,
        posSyncConnectionStatus,
        posSyncEnabled,
        publishSharedWeighing,
    ]);

    useEffect(() => {
        if (!posSyncEnabled || !isEditorPosView) return undefined;
        return () => {
            publishActivePurchaseId(null);
            publishSharedPreview(null);
            publishSharedWeighing(null);
        };
    }, [isEditorPosView, posSyncEnabled, publishActivePurchaseId, publishSharedPreview, publishSharedWeighing]);

    const handleItemPriceTypeChange = (item, nextTypeValue) => {
        if (!canOverrideLinePriceType || !canEditSale) return;
        const variety = findVarietyForItem(item);
        if (!variety) return;
        if (!varietySupportsBarcodePriceTypeSelection(variety)) {
            notify('Solo se puede cambiar el tipo de precio cuando la variedad comparte el mismo codigo de barras en todos sus precios.');
            return;
        }
        const currentIndex = Number.isInteger(item?.priceIndex)
            ? item.priceIndex
            : resolveItemPriceIndex(variety, item);
        if (!Number.isInteger(currentIndex) || currentIndex < 0) return;

        const currentKey = lineKey(variety.id, currentIndex);
        const normalizedType = normalizePriceTypeValue(nextTypeValue);
        if (!normalizedType) {
            const nextLineMeta = {
                ...linePriceMeta,
                [currentKey]: {
                    priceType: normalizePriceTypeValue(item?.priceType)
                        || resolvePriceClientType(variety.VarietyPrices?.[currentIndex]),
                    priceTypeOverride: false,
                    autoPriceSelection: Boolean(linePriceMeta[currentKey]?.autoPriceSelection),
                },
            };
            setLinePriceMeta(nextLineMeta);
            handleSave(
                false,
                quantities,
                totalAmount,
                discounts,
                null,
                null,
                discountAuthorizations,
                orderDiscountAuthorizationId,
                undefined,
                undefined,
                nextLineMeta
            );
            return;
        }

        const targetCandidates = (variety.VarietyPrices || [])
            .map((price, index) => ({ price, index }))
            .filter(({ price }) =>
                isPriceAllowedByRegister(price)
                && normalizePriceTypeValue(resolvePriceClientType(price)) === normalizedType
            )
            .sort((a, b) => Number(a.price?.price || 0) - Number(b.price?.price || 0));
        const targetIndex = targetCandidates[0]?.index;
        if (!Number.isInteger(targetIndex) || targetIndex < 0) return;

        if (targetIndex === currentIndex) {
            const nextLineMeta = {
                ...linePriceMeta,
                [currentKey]: {
                    priceType: normalizedType,
                    priceTypeOverride: true,
                    autoPriceSelection: false,
                },
            };
            setLinePriceMeta(nextLineMeta);
            handleSave(
                false,
                quantities,
                totalAmount,
                discounts,
                null,
                null,
                discountAuthorizations,
                orderDiscountAuthorizationId,
                undefined,
                undefined,
                nextLineMeta
            );
            return;
        }

        const currentQty = normalizeQuantity(
            quantities?.[variety.id]?.[currentIndex] || item?.quantity || 0
        );
        if (currentQty <= 0) return;

        const nextQuantities = { ...quantities };
        const nextDiscounts = { ...discounts };
        const nextAuthorizations = { ...discountAuthorizations };
        const nextOrder = [...addedOrder];
        const targetKey = lineKey(variety.id, targetIndex);

        nextQuantities[variety.id] = [...(nextQuantities[variety.id] || [])];
        nextDiscounts[variety.id] = [...(nextDiscounts[variety.id] || [])];
        nextAuthorizations[variety.id] = [...(nextAuthorizations[variety.id] || [])];

        const sourceDiscount = Number(nextDiscounts[variety.id][currentIndex] || 0);
        const sourceAuthorizationId = nextAuthorizations[variety.id][currentIndex] ?? null;

        nextQuantities[variety.id][targetIndex] = normalizeQuantity(
            (nextQuantities[variety.id][targetIndex] || 0) + currentQty
        );
        nextQuantities[variety.id][currentIndex] = 0;

        if (nextDiscounts[variety.id][targetIndex] == null) {
            nextDiscounts[variety.id][targetIndex] = sourceDiscount;
        }
        nextDiscounts[variety.id][currentIndex] = 0;

        if (nextAuthorizations[variety.id][targetIndex] == null) {
            nextAuthorizations[variety.id][targetIndex] = sourceAuthorizationId;
        }
        nextAuthorizations[variety.id][currentIndex] = null;

        const currentOrderIndex = nextOrder.findIndex(
            (entry) => entry.varietyId === variety.id && entry.priceIndex === currentIndex
        );
        if (currentOrderIndex !== -1) {
            nextOrder[currentOrderIndex] = { varietyId: variety.id, priceIndex: targetIndex };
        } else if (!nextOrder.some((entry) => entry.varietyId === variety.id && entry.priceIndex === targetIndex)) {
            nextOrder.unshift({ varietyId: variety.id, priceIndex: targetIndex });
        }

        setQuantities(nextQuantities);
        setDiscounts(nextDiscounts);
        setDiscountAuthorizations(nextAuthorizations);
        setAddedOrder(nextOrder);
        const nextLineMeta = { ...linePriceMeta };
        delete nextLineMeta[currentKey];
        nextLineMeta[targetKey] = {
            priceType: normalizedType,
            priceTypeOverride: true,
            autoPriceSelection: false,
        };
        setLinePriceMeta(nextLineMeta);

        const nextTotal = calculateTotal(nextQuantities, nextDiscounts);
        handleSave(
            false,
            nextQuantities,
            nextTotal,
            nextDiscounts,
            null,
            null,
            nextAuthorizations,
            orderDiscountAuthorizationId,
            undefined,
            undefined,
            nextLineMeta
        );
    };

    const isVarietySoldOut = (variety) => {
        const prices = variety?.VarietyPrices || [];
        if (!prices.length) return false;
        return prices.every((price) => price.isSoldOut);
    };

    const isProductSoldOut = (product) => {
        const varieties = product?.Varieties || [];
        if (!varieties.length) return false;
        return varieties.every((variety) => isVarietySoldOut(variety));
    };

    const handlePrint = async () => {
        if (!id) return;
        try {
            if (!branchId) {
                notify("Sucursal no definida.");
                return;
            }
            const printMode = getPrintMode();
            const useRemotePrint = printMode === PRINT_MODE_REMOTE;
            if (useRemotePrint) {
                sendPrintRequest(branchId, id);
            } else {
                const printUrl = `/branches/${branchId}/purchases/${id}/receipt/preview?autoprint=1`;
                const printWindow = window.open(printUrl, "_blank", "noopener,noreferrer");
                if (!printWindow) {
                    notify("No se pudo abrir la impresion local. Revisa el bloqueador de ventanas emergentes.");
                    return;
                }
            }
            const notification = document.createElement("div");
            notification.textContent = useRemotePrint
                ? "Imprimiendo comprobante..."
                : "Abriendo comprobante en el navegador...";
            notification.style.position = "fixed";
            notification.style.bottom = "20px";
            notification.style.right = "20px";
            notification.style.backgroundColor = "#4caf50";
            notification.style.color = "white";
            notification.style.padding = "10px";
            notification.style.borderRadius = "5px";
            notification.style.boxShadow = "0px 4px 6px rgba(0, 0, 0, 0.1)";
            notification.style.zIndex = "1010";
            document.body.appendChild(notification);

            setTimeout(() => {
                document.body.removeChild(notification);
            }, 3000);
        } catch (error) {
            console.error("Error al imprimir.", error);
            notify("Error al imprimir.");
        }
    };

    const cartFooterActions = (
        <div className="flex items-center gap-2 mt-2 flex-nowrap overflow-x-auto max-w-full">
            <div className="flex items-center gap-2 flex-nowrap shrink-0">
                {canEditSale && canSell && (
                    <button
                        onClick={() => setShowOrderDiscountModal(true)}
                        className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-3 rounded text-sm flex items-center gap-2"
                        title="Descuento total"
                        type="button"
                    >
                        <FiPercent />
                        <span className="hidden md:inline">Descuento</span>
                    </button>
                )}
                {canEditSale && canSell && canExcludeTaxes && (
                    <button
                        onClick={() => setShowTaxModal(true)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold p-3 rounded text-sm flex items-center justify-center disabled:opacity-60"
                        title="Excluir impuestos"
                        type="button"
                        disabled={taxesLoading || !!taxesError || taxes.length === 0}
                    >
                        <FiPercent />
                    </button>
                )}
            </div>
            <div className="ml-auto flex items-center gap-2 flex-nowrap shrink-0">
                {id && (
                    <button
                        onClick={handlePrint}
                        className="bg-slate-700 hover:bg-slate-800 text-white font-bold py-3 px-4 rounded text-sm flex items-center"
                        title="Imprimir"
                        type="button"
                    >
                        <FiPrinter />
                    </button>
                )}
                {canEditSale && (
                    <button
                        onClick={handleSuspendSale}
                        className="bg-orange-500 hover:bg-orange-600 text-white font-bold p-3 min-w-[44px] rounded text-sm flex items-center justify-center"
                        title="Suspender"
                        type="button"
                    >
                        <FiPauseCircle />
                    </button>
                )}
                {canEditSale && (
                    <button
                        onClick={handleFinishPurchase}
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-3 rounded text-sm flex items-center gap-2"
                        title="Finalizar"
                        type="button"
                    >
                        <FiCheckCircle />
                        <span className="hidden md:inline">Finalizar</span>
                    </button>
                )}
            </div>
        </div>
    );

    const renderVarietyResultCard = ({ product, variety }) => {
        const imageSrc = variety.varietyImage || product.productImage || 'https://placehold.co/200';
        const isSoldOut = isVarietySoldOut(variety);
        const isSelectable = !isSoldOut;

        if (productView === 'grid') {
            return (
                <div
                    key={`${product.id}-${variety.id}`}
                    className={`relative w-full max-w-[165px] p-3 bg-white rounded-lg shadow transition-shadow flex flex-col justify-between ${
                        isSelectable ? 'cursor-pointer hover:shadow-lg' : 'cursor-not-allowed'
                    } ${
                        isSoldOut ? 'opacity-70' : ''
                    }`}
                    onClick={() => {
                        if (!isSelectable) return;
                        handleVarietySelection({ product, variety });
                    }}
                >
                    {isSoldOut && (
                        <span className="absolute top-2 left-2 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-semibold uppercase text-white">
                            Agotado
                        </span>
                    )}
                    <div className="relative w-full h-[130px]">
                        <img
                            src={imageSrc}
                            alt={variety.varietyName || product.productName}
                            className="absolute inset-0 w-full h-full object-cover rounded"
                        />
                    </div>
                    <h3 className="text-sm font-semibold break-words mt-2">
                        {product.productName}
                        {variety.varietyName && (
                            <>
                                <br />
                                {variety.varietyName}
                            </>
                        )}
                    </h3>
                </div>
            );
        }

        return (
            <div
                key={`${product.id}-${variety.id}`}
                className={`flex items-center gap-4 p-3 bg-white rounded-lg shadow transition-shadow ${
                    isSelectable ? 'cursor-pointer hover:shadow-lg' : 'cursor-not-allowed'
                } ${
                    isSoldOut ? 'opacity-70' : ''
                }`}
                onClick={() => {
                    if (!isSelectable) return;
                    handleVarietySelection({ product, variety });
                }}
            >
                <img
                    src={imageSrc}
                    alt={variety.varietyName || product.productName}
                    className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                    <h3 className="text-sm sm:text-base font-semibold break-words">
                        {product.productName}
                    </h3>
                    {variety.varietyName && (
                        <p className="text-xs sm:text-sm text-gray-600 break-words">
                            {variety.varietyName}
                        </p>
                    )}
                </div>
                <div className="flex flex-col items-end gap-1">
                    {isSoldOut && (
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-red-700">
                            Agotado
                        </span>
                    )}
                    <span className="text-xs text-blue-600 font-semibold whitespace-nowrap">Ver detalle</span>
                </div>
            </div>
        );
    };

    const renderProductCard = (product) => {
        const imageSrc = product.productImage || 'https://placehold.co/200';
        const isSoldOut = isProductSoldOut(product);

        if (productView === 'grid') {
            return (
                <div
                    key={product.id}
                    className={`relative w-full max-w-[165px] p-3 bg-white rounded-lg shadow hover:shadow-lg transition-shadow flex flex-col justify-between cursor-pointer ${
                        isSoldOut ? 'opacity-70' : ''
                    }`}
                    onClick={() => {
                        setModalProduct(product);
                    }}
                >
                    {isSoldOut && (
                        <span className="absolute top-2 left-2 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-semibold uppercase text-white">
                            Agotado
                        </span>
                    )}
                    <div className="relative w-full h-[130px]">
                        <img
                            src={imageSrc}
                            alt={product.productName}
                            className="absolute inset-0 w-full h-full object-cover rounded"
                        />
                    </div>

                    <h3 className="text-sm font-semibold break-words mt-2">
                        {product.productName}<br />[Ver Detalle]
                    </h3>
                </div>
            );
        }

        return (
            <div
                key={product.id}
                className={`flex items-center gap-4 p-3 bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer ${
                    isSoldOut ? 'opacity-70' : ''
                }`}
                onClick={() => {
                    setModalProduct(product);
                }}
            >
                <img
                    src={imageSrc}
                    alt={product.productName}
                    className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                    <h3 className="text-sm sm:text-base font-semibold break-words">
                        {product.productName}
                    </h3>
                </div>
                <div className="flex flex-col items-end gap-1">
                    {isSoldOut && (
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-red-700">
                            Agotado
                        </span>
                    )}
                    <span className="text-xs text-blue-600 font-semibold whitespace-nowrap">Ver detalle</span>
                </div>
            </div>
        );
    };

    const handlePendingTabSelect = useCallback((selectedId) => {
        if (selectedId) {
            if (!canUpdateSale) return;
            navigate(buildPathWithCurrentSearch(`/branches/${branchId}/purchases/${selectedId}/edit`));
            return;
        }
        if (!canCreateSale) return;
        navigate(buildPathWithCurrentSearch(`/branches/${branchId}/purchases/new`));
    }, [
        branchId,
        buildPathWithCurrentSearch,
        canCreateSale,
        canUpdateSale,
        navigate,
    ]);

    const renderCategoryPicker = (itemsWrapperClassName = '') => (
        <CategoryFilterBar
            categories={displayCategories}
            selectedCategory={selectedCategory}
            onSelect={handleCategorySelect}
            includeAll
            allItem={{ id: 'all', label: 'Todos', initial: 'T' }}
            loading={categoriesLoading}
            loadingMessage={<InlineLoading label="Cargando categorías" />}
            error={categoriesError}
            emptyMessage="No hay categorías disponibles para esta caja."
            isEmpty={displayCategories.length === 0}
            itemsWrapperClassName={itemsWrapperClassName}
        />
    );

    const renderHelperHeader = () => (
        <div className="sticky top-24 z-20 bg-gray-100 px-4 pt-4">
            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">
                            POS compartido
                        </div>
                        <h2 className="mt-2 text-2xl font-semibold text-gray-900">
                            Vista cliente
                        </h2>
                        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                            <span>{helperActiveSaleLabel}</span>
                            <span>Conexión: {posSyncConnectionStatus === 'connected' ? 'conectada' : posSyncConnectionStatus}</span>
                            <span>{posSyncHasEditor ? 'Editor conectado' : 'Sin editor conectado'}</span>
                        </div>
                    </div>
                    <PosModeTabs value={posView} onChange={handlePosViewChange} />
                </div>
            </div>
        </div>
    );

    const renderHelperContent = () => (
        <div className="px-4 pb-6 pt-8">
            <PosClientView
                purchase={helperSharedPurchase}
                loading={helperSharedPurchaseLoading}
                error={helperSharedPurchaseError}
                connectionStatus={posSyncConnectionStatus}
                hasEditor={posSyncHasEditor}
                scaleState={scaleState}
                weighing={posSyncWeighing}
            />
        </div>
    );

    useEffect(() => {
        if (!isEditorPosView) return undefined;
        const timer = setTimeout(() => {
            barcodeInputRef.current?.focus();
            barcodeInputRef.current?.select();
        }, 1000);
        return () => clearTimeout(timer);
    }, [isEditorPosView]);

    return (
        <>
            {isEditorPosView ? (
                <div className="flex flex-col min-h-[90vh] bg-gray-100">
                    {canReadSales && (
                        <PendingSalesTabs
                            branchId={branchId}
                            activePurchaseId={Number(id)}
                            activePurchaseLiveId={editorActivePurchaseId}
                            activePurchaseLiveTotal={totalAmount}
                            onSelect={handlePendingTabSelect}
                            tone="navbar"
                            cashRegisterId={cashRegisterId}
                            cashRegisterSessionId={cashRegisterSessionId}
                            scopeReady={pendingTabsScopeReady}
                            maxOpenSales={activeCashRegister?.maxOpenSales ?? 5}
                            canDelete={canDeleteSale}
                            canCreate={canCreateSale}
                            canSelect={canUpdateSale}
                        />
                    )}
                    <DocumentHeader
                        stickyOffsetClassName="top-28"
                        title={`${orderLabel} N° ${id}`}
                        onTitleClick={openPurchaseModal}
                        showCartButton={false}
                        headerActions={
                            <div className={mobileSearchVisible ? 'hidden md:block' : ''}>
                                <div className="flex items-center gap-3">
                                    <ProductViewToggle
                                        value={productView}
                                        onChange={setProductView}
                                        allowedViews={POS_PRODUCT_VIEWS}
                                    />
                                    <PosModeTabs value={posView} onChange={handlePosViewChange} />
                                </div>
                            </div>
                        }
                        totalAmount={totalAmount}
                        totalAmountClassName="hidden md:inline text-blue-500"
                        clientName={name}
                        clientRut={rut}
                        clientGiro={giro}
                        clientType={clientType}
                        emptyClientLabel="Venta Detalle"
                        canChangeClientType={canChangeClientType}
                        clientTypeOptions={allowedClientTypeOptions}
                        allowClientTypeToggleOff
                        clientTypeTitle="Filtro de precio:"
                        emptyClientTypeValueLabel="Todos"
                        onSelectClientType={handleClientTypeChange}
                        onChangeClientType={() => {
                            setTypeSelection(clientType || '');
                            setShowTypeModal(true);
                        }}
                        clientActions={
                            <button
                                type="button"
                                onClick={handleOpenClientModal}
                                disabled={!canReadClient && !canCreateClient}
                                className={`text-xs px-2 py-1 rounded border ${
                                    !canReadClient && !canCreateClient
                                        ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                                        : 'border-blue-500 text-blue-600 hover:bg-blue-50'
                                }`}
                            >
                                {name ? 'Cambiar cliente' : 'Agregar cliente'}
                            </button>
                        }
                        clientSectionClassName={
                            mobileSearchVisible
                                ? 'hidden md:block min-w-0 lg:flex-1'
                                : 'min-w-0 lg:flex-1'
                        }
                        middleContent={
                            isSlotProductView ? null : (
                                <div className="w-full max-w-[560px]">
                                    {renderCategoryPicker()}
                                </div>
                            )
                        }
                        rightContent={
                            <>
                                <div className={`${mobileSearchVisible ? 'block' : 'hidden'} md:block`}>
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        placeholder="Buscar producto..."
                                        value={searchTerm}
                                        onChange={handleSearch}
                                        onKeyDown={handleQuickEntryKeyDown}
                                        onFocus={(event) => {
                                            activateProductSearch();
                                            event.target.select();
                                        }}
                                        className="w-full px-3 py-2.5 text-base md:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                {mobileSearchVisible && !name && (
                                    <div className="mt-2 flex justify-end md:hidden">
                                        <button
                                            type="button"
                                            onClick={handleOpenClientModal}
                                            disabled={!canReadClient && !canCreateClient}
                                            className={`text-xs px-2 py-1 rounded border ${
                                                !canReadClient && !canCreateClient
                                                    ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                                                    : 'border-blue-500 text-blue-600 hover:bg-blue-50'
                                            }`}
                                        >
                                            Agregar cliente
                                        </button>
                                    </div>
                                )}
                                <div className={mobileSearchVisible ? 'hidden md:block' : 'block'}>
                                    <div className="mt-2 text-xs text-gray-700 flex items-center justify-between">
                                        <span>
                                            <span className="font-semibold">Caja:</span>{' '}
                                            {activeCashRegister ? activeCashRegister.name : 'Sin caja'}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <ScaleStatusPanel
                                                branchId={branchId}
                                                cashRegisterId={cashRegisterId}
                                                cashRegisterSessionId={cashRegisterSessionId}
                                                isSessionOwnedByUser={isActiveSessionOwnedByUser}
                                                scaleState={scaleState}
                                            />
                                            <span>
                                                {cashRegisterSessionId ? `Sesion #${cashRegisterSessionId}` : 'Sin sesion'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </>
                        }
                    />

                    <div className="relative flex">
                        <div className={cartWrapperClassName}>
                            <CartPanel
                                items={cartItems}
                                getVariety={findVarietyForItem}
                                getItemQuantity={getItemQuantity}
                                onItemClick={(item, variety) => {
                                    if (!variety) return;
                                    const price = variety.VarietyPrices?.[item.priceIndex];
                                    if (isScaleInputPrice(price)) {
                                        setActiveScaleSelection(null);
                                    }
                                    setSlotModalState(null);
                                    setModalVariety(variety);
                                    setModalVarietyPrice(null);
                                }}
                                onRemoveItem={handleRemoveItem}
                                onQuantityChange={handleQuantityChange}
                                onQuantityIncrement={handleQuantityIncrement}
                                canEdit={canEditSale}
                                pendingItemDiscounts={pendingDiscountRequests.items}
                                pendingOrderDiscount={pendingDiscountRequests.order}
                                onResumePendingItemDiscount={resumeDiscountAuthorization}
                                onResumePendingOrderDiscount={resumeDiscountAuthorization}
                                canOverridePriceType={canEditSale && canOverrideLinePriceType}
                                getItemPriceTypeOptions={getItemPriceTypeOptions}
                                onItemPriceTypeChange={handleItemPriceTypeChange}
                                getItemPricing={resolveCartItemPricing}
                                headerContent={
                                    <input
                                        ref={barcodeInputRef}
                                        type="text"
                                        placeholder="Nombre o código de barras..."
                                        value={searchTerm}
                                        onChange={handleSearch}
                                        onKeyDown={handleQuickEntryKeyDown}
                                        onFocus={(event) => {
                                            activateProductSearch();
                                            event.target.select();
                                        }}
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                }
                                panelClassName={cartPanelClassName}
                                footerClassName="flex flex-col gap-1 mt-4 bottom-0 pb-1"
                                itemsSubtotal={itemsSubtotal}
                                orderDiscountPercentage={orderDiscountPercentage}
                                orderDiscountAmount={orderDiscountAmount}
                                totalAmount={totalAmount}
                                footerActions={cartFooterActions}
                            />
                        </div>
                    </div>

                    <div className={mainContentClassName}>
                        <div className="max-w-7xl mx-auto">
                            {!isSlotProductView && !mobileSearchVisible && (
                                <div className="mb-4 lg:hidden">
                                    {renderCategoryPicker()}
                                </div>
                            )}

                            {isSlotProductView ? (
                                <div className="space-y-2">
                                    <div className="text-xs font-medium text-gray-500">
                                        Favs guardados para la caja actual.
                                    </div>
                                    <PosSlotGrid
                                        slots={resolvedPosSlots}
                                        onSelect={handleFavoriteProductSelect}
                                        onAddSlot={handleAddPosSlot}
                                        showAddSlot
                                        emptyLabel="No hay favs configurados todavía."
                                    />
                                </div>
                            ) : (
                                <>
                                    {searchTerm && searchVarietyResults.length > 0 && (
                                        <div className="space-y-4 mb-4">
                                            <ProductGrid view={productView}>
                                                {searchVarietyResults.map(renderVarietyResultCard)}
                                            </ProductGrid>
                                        </div>
                                    )}

                                    <div className="space-y-8">
                                        {categoriesToRender.map((category) => {
                                            const productsInCategory = filteredProducts
                                                .filter((p) => resolveProductCategoryId(p) === category.id)
                                                .sort((a, b) => a.productName.localeCompare(b.productName));

                                            if (productsInCategory.length === 0) return null;

                                            return (
                                                <div key={category.id}>
                                                    <div className="flex items-center gap-4 my-2">
                                                        <div className="flex-grow border-t border-gray-300"></div>
                                                        <h2 className="text-lg font-bold text-gray-700 whitespace-nowrap">
                                                            {category.name || 'Categoria'}
                                                        </h2>
                                                        <div className="flex-grow border-t border-gray-300"></div>
                                                    </div>

                                                    <ProductGrid view={productView}>
                                                        {productsInCategory.map(renderProductCard)}
                                                    </ProductGrid>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="min-h-[90vh] bg-gray-100">
                    {renderHelperHeader()}
                    {renderHelperContent()}
                </div>
            )}
            {isEditorPosView && !showPurchaseModal && (
                <button
                    type="button"
                    onClick={toggleMobileSearch}
                    disabled={mobileSearchVisible && hasActiveSearch}
                    className={`md:hidden fixed bottom-6 right-4 z-40 rounded-full shadow-lg p-3 ${
                        mobileSearchVisible && hasActiveSearch
                            ? 'bg-blue-300 text-white cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                    aria-label={mobileSearchVisible ? 'Ocultar búsqueda' : 'Mostrar búsqueda'}
                >
                    {mobileSearchVisible && !hasActiveSearch ? <FiX size={20} /> : <FiSearch size={20} />}
                </button>
            )}

            {/* Modal Component */}
            {modalVariety && (
                <VarietyDetailModal
                    variety={modalVariety}
                    onClose={closeModalVariety}
                    priceFilter={(price) => isPriceAllowedForType(clientType, price)}
                    resolveDisplayPrice={resolveVarietyPriceDisplay}
                    quantities={quantities}
                    discounts={discounts}
                    activeScaleSelection={activeScaleSelection}
                    scaleWeightKg={scaleState?.freshStableWeightKg ?? scaleState?.latestStableWeightKg ?? lastStableScaleReading?.weightKg ?? null}
                    onPriceIncrement={canEditSale && canSell ? handlePriceCardIncrement : null}
                    onPriceDecrement={canEditSale && canSell ? handlePriceCardDecrement : null}
                    onPriceKeypad={canEditSale && canSell ? handlePriceCardKeypad : null}
                    onConfirmClose={closeAllProductModals}
                />
            )}
            <Modal
                isOpen={!!modalVarietyPrice}
                onClose={closeModal}
                closeModalVariety={closeModalVariety}
                closeModalProduct={() => {
                    setModalProduct(null); setTimeout(() => {
                        searchInputRef?.current?.focus();
                    }, 100);
                }}
                variety={modalVariety}
                edit={canEditSale}
                quantities={quantities}
                varietyPrice={modalVarietyPrice}
                handleQuantityChange={handleModalQuantityChange}
                handleQuantityIncrement={handleQuantityIncrement}
                setShowDiscountModal={setShowDiscountModal}
                discounts={discounts}
                canDiscount={canEditSale && canSell}
                resolveDisplayPrice={resolveVarietyPriceDisplay}
                onConfirmQuantity={slotModalState ? handleConfirmSlotQuantity : null}
                initialQuantity={slotModalState?.initialQuantity ?? null}
                confirmLabel={slotModalState?.quantityMode === 'set' ? 'Actualizar' : 'Agregar'}
                updateOnChange={Boolean(slotModalState)}
            />
            <DiscountModal
                isOpen={showDiscountModal}
                onClose={() => setShowDiscountModal(false)}
                variety={modalVariety}
                varietyPrice={modalVarietyPrice}
                handleDiscountChange={handleDiscountChange}
                discounts={discounts}
                closeEverything={() => {
                    setModalVarietyPrice(null);
                    setModalVariety(null);
                    setModalProduct(null);
                    setSlotModalState(null);
                    setShowDiscountModal(false);
                    setTimeout(() => {
                        searchInputRef?.current?.focus();
                    }, 100);
                }}
            />
            <OrderDiscountModal
                isOpen={showOrderDiscountModal}
                onClose={() => setShowOrderDiscountModal(false)}
                itemsSubtotal={itemsSubtotal}
                orderDiscountAmount={orderDiscountAmount}
                orderDiscountPercentage={orderDiscountPercentage}
                onApply={handleOrderDiscountApply}
            />
            <TaxExclusionModal
                isOpen={showTaxModal}
                onClose={() => setShowTaxModal(false)}
                taxes={taxes}
                excludedTaxIds={excludedTaxIds}
                onChange={(ids) => setExcludedTaxIds(ids)}
                disabled={!canExcludeTaxes || taxesLoading || !!taxesError}
            />
            <DocumentTypeModal
                isOpen={showDocumentTypeModal}
                onClose={handleCloseDocumentTypeModal}
                onSelect={handleDocumentTypeSelect}
                canConvert={canConvertToQuotation}
            />
            <ModalClientRegistration
                isOpen={showClientModal}
                searchInputRef={searchInputRef}
                onClose={handleCloseClientModal}
                name={name}
                setName={setName}
                setClientId={setClientId}
                rut={rut}
                setRut={setRut}
                email={email}
                setEmail={setEmail}
                address={address}
                setAddress={setAddress}
                city={city}
                setCity={setCity}
                phone={phone}
                setPhone={setPhone}
                savePurchase={savePurchaseClient}
                setModalVarietyPrice={setModalVarietyPrice}
                onClientSelected={handleClientSelected}
                clientType={clientType}
                setClientType={(type) => {
                    const resolvedType = resolveAllowedClientType(type);
                    setClientType(resolvedType);
                    setTypeSelection(resolvedType);
                }}
                clientTypeOptions={allowedClientTypeOptions}
                requireClient={pendingDocumentType === 'factura' || pendingConversionAfterClient}
                canCreateClient={canCreateClient}
                canReadClient={canReadClient}
            />
            <PosSlotPickerModal
                isOpen={showSlotPickerModal}
                onClose={() => setShowSlotPickerModal(false)}
                entries={posSlotPickerEntries}
                onSelect={handleSlotAddSelection}
            />
            <DiscountAuthorizationModal
                isOpen={showDiscountAuthorizationModal}
                onClose={() => {
                    setShowDiscountAuthorizationModal(false);
                    setPendingDiscountAuthorization(null);
                    setDiscountAuthorizationInitialState(null);
                }}
                branchId={branchId}
                cashRegisterId={cashRegisterId}
                discountContext={pendingDiscountAuthorization}
                initialState={discountAuthorizationInitialState}
                onRequested={handleDiscountRequested}
                onAuthorized={(response) => {
                    if (!pendingDiscountAuthorization) return;
                    const authorizationId = response?.id ?? null;
                    if (pendingDiscountAuthorization.type === 'item') {
                        applyItemDiscount(
                            pendingDiscountAuthorization.varietyId,
                            pendingDiscountAuthorization.priceIndex,
                            pendingDiscountAuthorization.discountAmount || 0,
                            authorizationId
                        );
                    } else {
                        applyOrderDiscount(
                            pendingDiscountAuthorization.orderAmount ?? null,
                            pendingDiscountAuthorization.orderPercent ?? null,
                            authorizationId
                        );
                    }
                    clearPendingDiscount(pendingDiscountAuthorization);
                    setPendingDiscountAuthorization(null);
                    setShowDiscountAuthorizationModal(false);
                }}
            />
            {showTypeModal && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={handleCloseTypeModal}
                >
                    <div
                        className="bg-white rounded-lg shadow max-w-md w-full p-4 mx-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="text-lg font-bold">Seleccionar tipo de venta</h3>
                            <button onClick={handleCloseTypeModal} className="text-gray-600 hover:text-black">✕</button>
                        </div>
                        <div className="flex flex-col gap-3">
                            <div className="flex flex-wrap gap-1 border-b border-gray-200">
                                {allowedClientTypeOptions.length === 0 ? (
                                    <span className="text-sm text-gray-500">No definido</span>
                                ) : (
                                    allowedClientTypeOptions.map((option) => {
                                        const isActive = typeSelection === option.value;
                                        return (
                                            <button
                                                key={option.value}
                                                type="button"
                                                onClick={() => setTypeSelection(option.value)}
                                                className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px rounded-t-md transition ${
                                                    isActive
                                                        ? 'bg-blue-50 text-blue-700 border-blue-600'
                                                        : 'bg-transparent text-gray-600 border-transparent hover:text-gray-800 hover:bg-gray-50'
                                                }`}
                                            >
                                                {option.label}
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                            <div className="flex justify-end gap-2 mt-2">
                                <button
                                    onClick={handleCloseTypeModal}
                                    className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 text-sm"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={() => {
                                        handleClientTypeChange(typeSelection);
                                        setShowTypeModal(false);
                                    }}
                                    className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                                >
                                    Confirmar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <PurchaseDetailsModal
                isOpen={showPurchaseModal}
                onClose={() => setShowPurchaseModal(false)}
                purchase={
                    purchaseDetails
                        ? { ...purchaseDetails, purchaseItems: cartItems }
                        : purchaseDetails
                }
                products={products}
                openProductModal={(variety) => {
                    setSlotModalState(null);
                    setModalVariety(variety);
                    setModalVarietyPrice(null);
                }}
                closeProductModal={() => {
                    setSlotModalState(null);
                    setModalVariety(null);
                    setModalVarietyPrice(null);
                }}
                handleRemoveItem={handleRemoveItem} // ✅ Pass the function
                onQuantityChange={handleQuantityChange}
                onQuantityIncrement={handleQuantityIncrement}
                handleFinishPurchase={handleFinishPurchase}
                canEditItems={canEditSale}
                canFinalize={canEditSale}
                onSuspendPurchase={handleSuspendSale}
                canSuspend={canEditSale}
                pendingItemDiscounts={pendingDiscountRequests.items}
                pendingOrderDiscount={pendingDiscountRequests.order}
                onResumePendingItemDiscount={resumeDiscountAuthorization}
                onResumePendingOrderDiscount={resumeDiscountAuthorization}
                getItemPricing={resolveCartItemPricing}
                footerActions={cartFooterActions}
            />
            {/* Product Modal */}
            {modalProduct && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-30" onClick={() => {
                    setModalProduct(null); setTimeout(() => {
                        searchInputRef?.current?.focus();
                    }, 100);
                }}>
                    <div
                        className="bg-white p-6 rounded-lg shadow-lg max-w-3xl w-full mx-4 relative flex flex-col"
                        style={{ maxHeight: "80vh", display: "flex" }} // Prevents overflowing the screen
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Scrollable Content */}
                        <div className="overflow-y-auto px-2" style={{ maxHeight: "70vh" }}>
                            {/* Close Button */}
                            <button onClick={() => {
                                setModalProduct(null); setTimeout(() => {
                                    searchInputRef?.current?.focus();
                                }, 100);
                            }} className="absolute top-2 right-2 text-gray-600 hover:text-black bg-white p-1 rounded-full">
                                ✕
                            </button>

                            {/* Centered Image with Increased Size */}
                            <div className="flex justify-center items-center mb-4">
                                <img
                                    src={modalProduct.productImage || "https://placehold.co/300"}
                                    alt={modalProduct.productName}
                                    className="w-32 h-32 object-cover rounded-lg"
                                />
                            </div>

                            <h2 className="text-xl font-bold text-center">Variedades de {modalProduct.productName}</h2>

                            {/* Varieties Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                {modalProduct.Varieties.filter((variety) => hasSellablePrice(variety, clientType)).map((variety) => {
                                    // Get Highest and Lowest Prices
                                    const sellablePrices = variety.VarietyPrices.filter((price) =>
                                        isPriceAllowedForType(clientType, price)
                                    );
                                    const pricedEntries = sellablePrices.map((entry) => {
                                        const display = resolveVarietyPriceDisplay(variety, entry, 0);
                                        return {
                                            entry,
                                            shownPrice: Number(display?.price ?? entry.price),
                                            taxFreeApplied: Boolean(display?.taxFreeApplied),
                                        };
                                    });
                                    const shownPrices = pricedEntries.map((entry) => entry.shownPrice);
                                    const highestPrice = Math.max(...shownPrices);
                                    const lowestPrice = Math.min(...shownPrices);
                                    const highestPriceUnit = pricedEntries.find((entry) => entry.shownPrice === highestPrice)?.entry?.metricUnit || '';
                                    const lowestPriceUnit = pricedEntries.find((entry) => entry.shownPrice === lowestPrice)?.entry?.metricUnit || '';
                                    const hasTaxFreePrices = pricedEntries.some((entry) => entry.taxFreeApplied);
                                    const isSoldOut = isVarietySoldOut(variety);
                                    const isSelectable = !isSoldOut;

                                    return (
                                        <div
                                            key={variety.id}
                                            className={`relative bg-white border rounded-lg shadow p-4 ${
                                                isSelectable ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed'
                                            } ${
                                                isSoldOut ? 'opacity-70' : ''
                                            }`}
                                            onClick={() => {
                                                if (!isSelectable) return;
                                                handleVarietySelection({ variety });
                                            }}
                                        >
                                            {isSoldOut && (
                                                <span className="absolute top-2 right-2 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-red-700">
                                                    Agotado
                                                </span>
                                            )}
                                            <div className="flex items-center gap-4 md:gap-8 mb-2">
                                                {/* Image */}
                                                <img
                                                    src={variety.varietyImage || "https://placehold.co/200"}
                                                    alt={variety.varietyName}
                                                    className="w-16 h-16 object-cover rounded-lg shadow"
                                                />
                                                {/* Variety Name */}
                                                <p className="text-sm font-semibold break-words mt-2">{variety.varietyName}</p>
                                            </div>

                                            <div className="flex justify-between text-sm">
                                                {/* Show Prices or Sold Out */}
                                                {highestPrice === lowestPrice ? (
                                                    <p className="text-gray-700 text-sm mt-1 inline-flex items-center">
                                                        ${highestPrice} {highestPriceUnit}
                                                        {hasTaxFreePrices && (
                                                            <span
                                                                className="ml-1 inline-flex text-blue-700"
                                                                title="Precio ajustado sin impuestos excluidos."
                                                                aria-label="Precio ajustado sin impuestos excluidos."
                                                            >
                                                                <FiInfo size={13} />
                                                            </span>
                                                        )}
                                                    </p>
                                                ) : shownPrices.length > 0 && (
                                                    <>
                                                        <p className="text-gray-700 text-sm mt-1 inline-flex items-center">
                                                            ${highestPrice} {highestPriceUnit}
                                                            {hasTaxFreePrices && (
                                                                <span
                                                                    className="ml-1 inline-flex text-blue-700"
                                                                    title="Precio ajustado sin impuestos excluidos."
                                                                    aria-label="Precio ajustado sin impuestos excluidos."
                                                                >
                                                                    <FiInfo size={13} />
                                                                </span>
                                                            )}
                                                        </p>
                                                        <p className='text-gray-700 text-sm mt-1 inline-flex items-center'>
                                                            ${lowestPrice} {lowestPriceUnit}
                                                            {hasTaxFreePrices && (
                                                                <span
                                                                    className="ml-1 inline-flex text-blue-700"
                                                                    title="Precio ajustado sin impuestos excluidos."
                                                                    aria-label="Precio ajustado sin impuestos excluidos."
                                                                >
                                                                    <FiInfo size={13} />
                                                                </span>
                                                            )}
                                                        </p>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default EditPurchasePage;
